/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * ------------------------------------------------------------
 * PDP configurator UI — E3 (recosi-qzf.3)
 *
 * Builds the Base-PC configurator inside the <configurator-form> mount that
 * E2 (snippets/product-configurator.liquid) renders. Reads the serialized
 * data island, groups the compatible components into the upgrade dimensions
 * (RAM/SSD/GPU · OS/Office/warranty · gift), renders one control per dimension,
 * and dispatches its selection state so the other engines can plug in without
 * re-reading the DOM:
 *
 *   configurator:ready   once, after the first render
 *   configurator:change  on every selection change
 *   detail = { configurator, base, selections{group->component|null},
 *              selectedComponents[], groups[] }
 *
 * LAYOUT REBUILT 2026-09-10 to the client design (2026-09-08 handoff, review
 * 2026-09-09). It was a flat list of radio groups; a Base PC with seven
 * dimensions and eight Office options made that a wall of buttons with the
 * price pushed off screen. Now:
 *
 *   - a live spec STRIP across the top (RAM / SSD / OS / warranty);
 *   - each group is a COLLAPSED ROW reading "name" over "current choice · +delta",
 *     with Spremeni / Zapri, and only ONE group open at a time;
 *   - inside an open group, three options then "Prikažite vse možnosti (N)";
 *   - option index 0 is always "keep what is installed, +0 €" — no group may
 *     default to something that costs money;
 *   - a "Dodajte k naročilu" divider splits core specs from extras;
 *   - a STICKY FOOTER carrying E4's total (instalments come from Leanpay's own widget).
 *
 * The restructure deliberately preserved every DOM hook the other engines query
 * ([data-configurator-option|-price|-stock|-none|-summary|-total], the radio
 * class, [data-configurator-group-selected]), so E4/E5/E6 needed no change.
 *
 * SCOPE: UI + selection state only. Price (E4/recosi-qzf.4), stock +
 * compatibility (E5/recosi-qzf.5) and the nested cart add (E6/recosi-qzf.6)
 * subscribe to the events above and fill the empty per-option / summary hooks;
 * this file renders no price, stock or availability.
 *
 * Defined as a custom element so it re-upgrades automatically after a
 * <product-rerender> swap. IIFE-wrapped because Shopify concatenates theme JS
 * into one bundle (see CLAUDE.md — avoid globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var ELEMENT_NAME = 'configurator-form';
  if (window.customElements && customElements.get(ELEMENT_NAME)) return;

  // Canonical dimension order. Unknown groups keep their first-seen order after these.
  // Extended 2026-09-14 from the Magento bundle option titles the 355 configurable
  // machines actually use (store/import/extract_bundles.py). A key missing here still
  // renders -- it just sorts last and falls back to the 'Nadgradnja' label -- so the
  // cost of forgetting one is a badly placed row, not a broken configurator.
  var GROUP_ORDER = ['ram', 'ssd', 'gpu', 'os', 'keyboard_layout', 'warranty',
                     'office', 'mouse', 'kbd_mouse_wired', 'kbd_mouse_wireless',
                     'kbd_mouse_set', 'monitor', 'extras', 'gift'];

  // What the machine IS, versus what you add to the order. The design draws a
  // "Dodajte k naročilu" divider between the two runs of groups.
  // keyboard_layout is core, not an add-on: the SLO sticker/reprint choice changes the
  // machine you are handed, the same way the SSD does. Office and the peripherals do not.
  var CORE_GROUPS = ['ram', 'ssd', 'gpu', 'os', 'keyboard_layout', 'warranty'];

  // The dimensions the live spec strip across the top of the card reports.
  var SUMMARY_KEYS = ['ram', 'ssd', 'os', 'warranty'];

  // Groups whose free option is preselected and whose "keep standard" row is dropped.
  // Why the keyboard is one: docs/PR-fix-pdp-client-review-2026-09.md.
  var FREE_DEFAULT_GROUPS = ['keyboard_layout'];

  // A gift is a free component whose handle starts with "darilo"; titles are translated. See docs/gifts.md.
  var GIFT_HANDLE = /^darilo/i;

  // "Windows 11 Home" (the machine) and "Win 11 Home" (the upgrade) are the same thing, and
  // offering it as an upgrade is what the client flagged on 2026-09-19. Normalising drops the
  // brand spelling and the word "garancije", which is the other pair that differs in wording
  // only: base "1 leto" vs option "1 leto garancije".
  function specKey(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/windows/g, 'win')
      .replace(/garancij\w*|jamstv\w*/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Rows shown before "Prikažite vse možnosti (N)". Three is the design's number and it is
  // the reason a group with eight options stays usable.
  var VISIBLE_OPTIONS = 3;

  // English fallback so a missing product.configurator.* key never breaks the render.
  var FALLBACK_LABELS = {
    title: 'Configure your PC',
    subtitle: '',
    summary_title: 'Your configuration',
    base_label: 'Base machine',
    none: 'Keep standard',
    group_ram: 'Memory (RAM)',
    group_ssd: 'Storage (SSD)',
    group_gpu: 'Graphics',
    group_os: 'Operating system',
    group_office: 'Office software',
    group_warranty: 'Warranty',
    group_gift: 'Free gift',
    group_other: 'Upgrade',
    change: 'Change',
    close: 'Close',
    show_all: 'Show all options ({{ count }})',
    recommended: 'Recommended',
    included: 'Included',
    without: 'None',
    addons_title: 'Add to your order',
    addons_note: '',
    total_label: 'Total for the selected configuration',
    linked_note: '',
    summary_ram: 'RAM',
    summary_ssd: 'SSD',
    summary_os: 'OS',
    summary_warranty: 'Warranty'
  };

  var uid = 0;

  function parseIsland(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    return node;
  }

  // Edit mode (recosi-yey; ADR-0001 "edit = remove + re-add"). The cart's "Uredi
  // konfiguracijo" link (mini-cart.liquid / main-cart.liquid) re-opens this PDP with the
  // original build encoded on the query string: the parent + child line KEYS (so the nested
  // add can zero the original bundle first) and the selected component VARIANT ids (so the
  // selection is restored). Returns null on a normal PDP visit. URLSearchParams decodes the
  // Liquid `url_encode`, so keys arrive with their ':' intact; ids/keys never contain ','.
  function readEditState() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('edit_configuration') !== '1') return null;
      function list(name) {
        return (params.get(name) || '').split(',').filter(function (v) { return v !== ''; });
      }
      return {
        active: true,
        parentKey: params.get('edit_parent') || '',
        childKeys: list('edit_children'),
        components: list('edit_components')
      };
    } catch (e) {
      return null;
    }
  }

  class ConfiguratorForm extends HTMLElement {
    connectedCallback() {
      if (this._ready) return; // guard against a spurious re-connect of the same instance
      var data = parseIsland(this, 'script[data-configurator-data]');
      if (!data || !data.base || !Array.isArray(data.components) || data.components.length === 0) return;

      this.data = data;
      this.labels = Object.assign({}, FALLBACK_LABELS, parseIsland(this, 'script[data-configurator-labels]') || {});
      this.uid = 'cfg-' + (++uid) + '-' + (data.base.id || '');
      this.selections = {}; // groupKey -> component | null (null = keep standard)
      // Parsed BEFORE the ready emit so E6's buy-button wiring sees the edit flag; the prior
      // selection is restored AFTER the render (below) so E4/E5 recompute against it.
      this.editState = readEditState();

      this.groups = this._groupComponents(data.components);
      if (this.groups.length === 0) return;

      this._render();
      this._applyPreselections();
      this._ready = true;
      this._emit('configurator:ready');
      this._restoreEditSelections();
    }

    // Bucket components by their machine group keys (custom.component_category, recosi-bhb),
    // preserving island order within a bucket, then order the buckets canonically. `groups` is a
    // multi-select list, so a component can land in several dimensions; it falls back to the
    // single `group` (legacy component:<x> tag) then 'other'. Duplicate keys per component are
    // ignored so it never appears twice in one dropdown.
    _groupComponents(components) {
      var byKey = {};
      var order = [];
      components.forEach(function (c) {
        var keys = (Array.isArray(c.groups) && c.groups.length) ? c.groups : [c.group || 'other'];
        var seen = {};
        keys.forEach(function (key) {
          if (seen[key]) return;
          seen[key] = true;
          if (!byKey[key]) { byKey[key] = { key: key, options: [] }; order.push(key); }
          byKey[key].options.push(c);
        });
      });
      // Drop a free option that is the machine's own spec under a different name; the group's
      // first row already offers exactly that, for free, as "keep standard".
      var self = this;
      order.forEach(function (key) {
        var base = specKey(self._baseSpec(key));
        if (!base) return;
        byKey[key].options = byKey[key].options.filter(function (c) {
          return !(c.price === 0 && specKey(c.title) === base);
        });
      });
      order = order.filter(function (key) { return byKey[key].options.length > 0; });

      order.sort(function (a, b) {
        var ia = GROUP_ORDER.indexOf(a);
        var ib = GROUP_ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return 0;   // both unknown -> keep first-seen (stable sort)
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
      return order.map(function (k) { return byKey[k]; });
    }

    groupLabel(key) {
      return this.labels['group_' + key] || this.labels.group_other || key;
    }

    _render() {
      var root = el('div', 'configurator', { 'data-configurator-ui': '' });

      if (this.labels.title) {
        var head = el('div', 'configurator__head');
        head.appendChild(el('h3', 'configurator__title heading h5')).textContent = this.labels.title;
        if (this.labels.subtitle) {
          head.appendChild(el('p', 'configurator__subtitle')).textContent = this.labels.subtitle;
        }
        root.appendChild(head);
      }

      var strip = this._renderStrip();
      if (strip) root.appendChild(strip);

      // Two runs of groups with a divider between them. The split is CORE_GROUPS (what the
      // machine IS — memory, disk, graphics, OS, warranty) versus everything else (what you
      // can add to the order). The design draws that line as "Dodajte k naročilu", and it is
      // the same line the buy decision has: changing the SSD changes the computer, adding
      // Office does not.
      var core = [], extra = [];
      this.groups.forEach(function (g) {
        (CORE_GROUPS.indexOf(g.key) === -1 ? extra : core).push(g);
      });

      var groupsWrap = el('div', 'configurator__groups');
      core.forEach(function (g) { groupsWrap.appendChild(this._renderGroup(g)); }, this);

      if (extra.length) {
        var divider = el('div', 'configurator__addons-head');
        divider.appendChild(el('div', 'configurator__addons-title')).textContent = this.labels.addons_title;
        if (this.labels.addons_note) {
          divider.appendChild(el('p', 'configurator__addons-note')).textContent = this.labels.addons_note;
        }
        groupsWrap.appendChild(divider);
        extra.forEach(function (g) { groupsWrap.appendChild(this._renderGroup(g)); }, this);
      }
      root.appendChild(groupsWrap);

      root.appendChild(this._renderSummary());
      this.appendChild(root);

      // EVERY GROUP CLOSED on load (client, 2026-09-10). The handoff opens SSD by default
      // and that is what shipped first; the client asked for all closed on seeing it. They
      // are right on this page: the card carries up to seven groups, and one pre-opened
      // group pushes the total and the add button below the fold on a laptop, so the first
      // thing a shopper sees is a half-answered question instead of a price.
      this._relocateActions();
    }

    // Move the form's REAL quantity picker and buy button into the card footer, where the
    // design puts them.
    //
    // Moved, not rebuilt. A second stepper would need syncing to the input E6 actually
    // reads (`form [name="quantity"]`), and a second button would need its own copy of E6's
    // validity gate, disabled state, error slot and edit-mode relabelling. Relocating the
    // originals means there is exactly one of each, E6 needed no change at all — its
    // interception is `event.target.closest('buy-button')`, which still matches — and the
    // page keeps working without this method if it ever fails to find them.
    //
    // Only <buy-button> moves, not the whole buy-buttons block: the express-checkout button
    // is its sibling and stays put (already hidden for a configured product by
    // .product-form--has-configurator). Re-runs on a <product-rerender> variant swap because
    // connectedCallback calls it again.
    _relocateActions() {
      var summary = this.querySelector('[data-configurator-summary]');
      if (!summary) return;
      var form = this.closest('form[action*="/cart/add"]');
      if (!form) return;

      var qty = form.querySelector('.product-form__info-item--quantity');
      var buy = form.querySelector('buy-button');
      if (!qty && !buy) return;

      var row = el('div', 'configurator__actions');
      if (qty) row.appendChild(qty);
      if (buy) row.appendChild(buy);

      // Before the linked-note paragraph, so the note reads as a footnote to the button.
      var note = summary.querySelector('.configurator__linked-note');
      if (note) summary.insertBefore(row, note);
      else summary.appendChild(row);
    }

    // The strip is built before the groups are, so a group that preselected an option for
    // itself (the gift, the keyboard) has to write its value back into it.
    _applyPreselections() {
      Object.keys(this.selections).forEach(function (key) {
        var component = this.selections[key];
        if (!component) return;
        var stripValue = this.querySelector('[data-strip-value="' + key + '"]');
        if (!stripValue) return;
        stripValue.textContent = component.title;
        stripValue.classList.add('is-upgraded');
      }, this);
    }

    // The live spec strip across the top. Shows only the dimensions this machine actually
    // reports, so an accessory with two upgrade groups does not render four empty cells.
    _renderStrip() {
      var self = this;
      var cells = SUMMARY_KEYS.filter(function (key) {
        return self._baseSpec(key) || self.groups.some(function (g) { return g.key === key; });
      });
      if (cells.length === 0) return null;

      var strip = el('div', 'configurator__strip');
      cells.forEach(function (key) {
        var cell = el('div', 'configurator__strip-cell', { 'data-strip-cell': key });
        cell.appendChild(el('span', 'configurator__strip-key')).textContent =
          self.labels['summary_' + key] || self.groupLabel(key);
        var value = el('span', 'configurator__strip-value', { 'data-strip-value': key });
        value.textContent = self._baseSpec(key) || '-';
        cell.appendChild(value);
        strip.appendChild(cell);
      });
      return strip;
    }

    // What the machine already has in this dimension, from the base.specs island block.
    _baseSpec(key) {
      var specs = (this.data.base && this.data.base.specs) || {};
      var v = specs[key];
      return (typeof v === 'string' && v.trim()) ? v.trim() : '';
    }

    _renderGroup(group) {
      var bodyId = this.uid + '-' + group.key + '-body';
      var nameId = this.uid + '-' + group.key + '-name';

      // role=group + aria-labelledby rather than fieldset/legend: the header is a disclosure
      // BUTTON, and a button inside a <legend> is not reliably operable across screen readers.
      var wrap = el('div', 'configurator__group', {
        'data-group': group.key, role: 'group', 'aria-labelledby': nameId
      });

      // See FREE_DEFAULT_GROUPS. No free option -> the ordinary "keep standard" row stays.
      var freeDefault = null;
      var gift = null;
      group.options.forEach(function (c) {
        if (c.price !== 0 || c.available === false) return;
        if (!freeDefault && FREE_DEFAULT_GROUPS.indexOf(group.key) !== -1) freeDefault = c;
        if (!gift && GIFT_HANDLE.test(c.handle || '')) gift = c;
      });
      // A free DARILO is preselected but declinable, so the customer gets it by default the
      // way they do on recositech.com.
      var preselect = freeDefault || gift;
      if (preselect) this.selections[group.key] = preselect;

      var headBtn = el('button', 'configurator__group-head', {
        type: 'button', 'aria-expanded': 'false', 'aria-controls': bodyId
      });
      var heading = el('span', 'configurator__group-heading');
      var name = el('span', 'configurator__group-name', { id: nameId });
      name.textContent = this.groupLabel(group.key);
      var note = this.labels['group_' + group.key + '_note'];
      if (note) {
        var noteEl = el('span', 'configurator__group-note');
        noteEl.textContent = note;
        name.appendChild(noteEl);
      }
      var selected = el('span', 'configurator__group-selected', {
        'data-configurator-group-selected': ''
      });
      selected.textContent = preselect ? preselect.title : (this._baseSpec(group.key) || this.labels.none);
      heading.appendChild(name);
      heading.appendChild(selected);
      headBtn.appendChild(heading);

      var toggle = el('span', 'configurator__group-toggle', { 'data-configurator-toggle': '' });
      toggle.textContent = this.labels.change;
      headBtn.appendChild(toggle);
      headBtn.addEventListener('click', this._onToggleClick.bind(this, wrap));
      wrap.appendChild(headBtn);

      var body = el('div', 'configurator__group-body', { id: bodyId, hidden: '' });
      var options = el('div', 'configurator__options');
      var radioName = this.uid + '-' + group.key;

      // Synthetic "keep what is installed" option — always index 0, always free, never
      // pre-empted by a paid upgrade. The design is explicit that no group may default to
      // something that costs money.
      if (!freeDefault) {
        options.appendChild(this._renderOption({
          group: group.key, none: true, radioName: radioName, checked: !preselect,
          title: this._baseSpec(group.key) || this.labels.none,
          sub: this._baseSpec(group.key) ? this.labels.included : ''
        }));
      }

      // Cheapest upgrade first. Not a recommendation — just the order that puts the smallest
      // step up next to the thing it is stepping up from, which is what makes the first three
      // rows the useful ones when the rest are behind the disclosure.
      // Office lists Microsoft first, so it is not hidden behind "show all".
      var rank = function (c) { return group.key === 'office' && !/microsoft/i.test(c.title || '') ? 1 : 0; };
      var sorted = group.options.slice().sort(function (a, b) {
        return rank(a) - rank(b) || (a.price == null ? 0 : a.price) - (b.price == null ? 0 : b.price);
      });
      sorted.forEach(function (component) {
        options.appendChild(this._renderOption({
          group: group.key, component: component, title: component.title,
          sub: component.type || '', radioName: radioName, checked: component === preselect
        }));
      }, this);
      body.appendChild(options);

      // Three rows, then a disclosure. A group with eight options (the Office list) is
      // unusable as a flat list, and the tail is hidden rather than unrendered so E4's prices
      // and E5's stock gating still apply to every option from first paint.
      var rows = options.children;
      if (rows.length > VISIBLE_OPTIONS) {
        for (var i = VISIBLE_OPTIONS; i < rows.length; i++) rows[i].hidden = true;
        var more = el('button', 'configurator__show-all', { type: 'button' });
        more.textContent = (this.labels.show_all || 'Show all ({{ count }})')
          .replace('{{ count }}', rows.length);
        more.addEventListener('click', function () {
          for (var j = 0; j < rows.length; j++) rows[j].hidden = false;
          more.remove();
        });
        body.appendChild(more);
      }

      wrap.appendChild(body);
      return wrap;
    }

    _onToggleClick(wrap) {
      this._toggleGroup(wrap, wrap.getAttribute('data-open') !== 'true' ? true : false);
    }

    // One open at a time. Collapsing the others is what keeps the card a scannable list of
    // decisions rather than a wall of radio buttons.
    _toggleGroup(wrap, open) {
      var all = this.querySelectorAll('.configurator__group');
      for (var i = 0; i < all.length; i++) {
        var g = all[i];
        var isTarget = g === wrap;
        var shouldOpen = isTarget && open;
        g.setAttribute('data-open', shouldOpen ? 'true' : 'false');
        var head = g.querySelector('.configurator__group-head');
        var body = g.querySelector('.configurator__group-body');
        var toggle = g.querySelector('[data-configurator-toggle]');
        if (head) head.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        if (body) body.hidden = !shouldOpen;
        if (toggle) toggle.textContent = shouldOpen ? this.labels.close : this.labels.change;
      }
    }

    _renderOption(cfg) {
      var component = cfg.component;
      var label = el('label', 'configurator-option', { 'data-configurator-option': '', 'data-group': cfg.group });
      if (cfg.none) {
        label.setAttribute('data-configurator-none', '');
      } else {
        // Data hooks for E4 (price) / E5 (stock + compatibility). E3 sets them but renders nothing from them.
        label.setAttribute('data-variant-id', component.variantId);
        label.setAttribute('data-product-id', component.productId);
        if (component.sku != null) label.setAttribute('data-sku', component.sku);
        if (component.price != null) label.setAttribute('data-price', component.price);
        if (component.compareAt != null) label.setAttribute('data-compare-at', component.compareAt);
        label.setAttribute('data-available', component.available ? 'true' : 'false');
        label.setAttribute('data-stock-bearing', component.stockBearing ? 'true' : 'false');
        if (component.qty != null) label.setAttribute('data-qty', component.qty);
      }

      var radio = el('input', 'configurator-option__radio', {
        type: 'radio',
        name: cfg.radioName,
        value: component ? component.variantId : ''
      });
      // Detach from the wrapping <form 'product'> (this mount lives inside it): point `form`
      // at a non-existent id so these radios group by name but are NOT submitted with the base
      // add-to-cart. Selection reaches the cart through E6's nested add, not native submit.
      radio.setAttribute('form', this.uid + '-detached');
      if (cfg.checked) radio.checked = true;
      radio.addEventListener('change', this._onChange.bind(this, cfg.group, component || null));

      var frame = el('span', 'configurator-option__frame');

      var body = el('span', 'configurator-option__body');
      var titleRow = el('span', 'configurator-option__title-row');
      var title = el('span', 'configurator-option__title');
      title.textContent = cfg.title;
      titleRow.appendChild(title);
      // "Priporočamo" only when the component carries the flag — see the island for why this
      // is never derived.
      if (component && component.recommended) {
        titleRow.appendChild(el('span', 'configurator-option__pill')).textContent = this.labels.recommended;
      }
      body.appendChild(titleRow);
      if (cfg.sub) {
        body.appendChild(el('span', 'configurator-option__sub')).textContent = cfg.sub;
      }
      frame.appendChild(body);

      // Empty slots owned by E4 (price) + E5 (stock); hidden until they fill them.
      var aside = el('span', 'configurator-option__aside');
      aside.appendChild(el('span', 'configurator-option__price', { 'data-configurator-price': '', hidden: '' }));
      aside.appendChild(el('span', 'configurator-option__stock', { 'data-configurator-stock': '', hidden: '' }));
      frame.appendChild(aside);

      label.appendChild(radio);
      label.appendChild(frame);
      return label;
    }

    // The card's sticky footer. Keeps every hook the old summary aside carried
    // ([data-configurator-summary] for E6's error slot, [data-configurator-total] for E4), so
    // the price engine and the cart add did not change: only where the block sits and what it
    // looks like. The per-group recap list is gone — the strip at the top and the collapsed
    // group rows now say the same thing twice as close to the choice as it can be made.
    _renderSummary() {
      var summary = el('aside', 'configurator__summary', {
        'data-configurator-summary': '', 'aria-live': 'polite'
      });

      // E4 total hook. E3 renders NO price and no label of its own: renderTotal() injects
      // both a `.configurator__total-label` and a `.configurator__total-prices` into this
      // slot, so adding a second heading here would print "Skupaj za izbrano konfiguracijo"
      // twice. The slot is the whole row; the CSS lays it out label-left, amount-right.
      summary.appendChild(el('div', 'configurator__summary-total', {
        'data-configurator-total': '', hidden: ''
      }));

      if (this.labels.linked_note) {
        summary.appendChild(el('p', 'configurator__linked-note')).textContent = this.labels.linked_note;
      }
      return summary;
    }

    _onChange(groupKey, component) {
      this.selections[groupKey] = component; // null for the "keep standard" option

      var base = this._baseSpec(groupKey);
      var text = component ? component.title : (base || this.labels.none);
      var groupEl = this.querySelector('.configurator__group[data-group="' + groupKey + '"]');
      if (groupEl) {
        var legendValue = groupEl.querySelector('[data-configurator-group-selected]');
        if (legendValue) {
          // Append the delta E4 rendered into the chosen row, so the collapsed row reads
          // "512 GB NVMe SSD · +89 €". Read rather than recomputed: E4 owns money formatting
          // (store format, per-market VAT), and a second formatter here would drift from it.
          var checked = groupEl.querySelector('.configurator-option__radio:checked');
          var priceNode = checked && checked.closest('[data-configurator-option]')
            ? checked.closest('[data-configurator-option]').querySelector('[data-configurator-price]')
            : null;
          var delta = (priceNode && !priceNode.hidden) ? (priceNode.textContent || '').trim() : '';
          legendValue.textContent = delta ? text + ' · ' + delta : text;
        }
      }
      var stripValue = this.querySelector('[data-strip-value="' + groupKey + '"]');
      if (stripValue) {
        stripValue.textContent = text;
        stripValue.classList.toggle('is-upgraded', !!component);
      }

      this._emit('configurator:change');
    }

    _restoreEditSelections() {
      if (!this.editState || !this.editState.active || this.editState.components.length === 0) return;
      var self = this;
      this.editState.components.forEach(function (variantId) {
        self.groups.forEach(function (group) {
          group.options.forEach(function (component) {
            if (String(component.variantId) !== String(variantId)) return;
            var radio = self.querySelector('.configurator-option__radio[value="' + variantId + '"]');
            if (!radio || radio.disabled) return; // sold out since / unknown → keep standard
            radio.checked = true;
            self._onChange(group.key, component);
          });
        });
      });
    }

    // Public state — E4/E5/E6 read this off the event or the element directly.
    getState() {
      var selections = {};
      var selectedComponents = [];
      var seenVariants = {};
      this.groups.forEach(function (group) {
        var chosen = this.selections[group.key] || null;
        selections[group.key] = chosen;
        // A multi-category component (recosi-bhb) can be selected in more than one group; add it
        // to the flat set only once so E4 (price) / E5 (validity) / E6 (cart) never double-count
        // the same variant. `selections` still records it per-group for the legend/summary.
        if (chosen && !seenVariants[chosen.variantId]) {
          seenVariants[chosen.variantId] = true;
          selectedComponents.push(chosen);
        }
      }, this);
      return { base: this.data.base, selections: selections, selectedComponents: selectedComponents };
    }

    _emit(name) {
      var state = this.getState();
      this.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: {
          configurator: this,
          base: state.base,
          selections: state.selections,
          selectedComponents: state.selectedComponents,
          groups: this.groups
        }
      }));
    }
  }

  customElements.define(ELEMENT_NAME, ConfiguratorForm);
})();

/**
 * ------------------------------------------------------------
 * PDP configurator PRICE ENGINE — E4 (recosi-qzf.4)
 *
 * Subscribes to the E3 <configurator-form> selection events and renders the
 * live configured price into the empty hooks E3 leaves behind:
 *   [data-configurator-price]  per upgrade option — the marginal add cost ("+ €X",
 *                              struck compare-at when the component is on sale,
 *                              "Free" at zero).
 *   [data-configurator-total]  the running configured total in the summary —
 *                              gross + ex-VAT (brez DDV), struck regular when the
 *                              build carries savings.
 *
 * REUSE (per the task): the total is base + Σ selected components as additive
 * nested lines (ADR-0001 / E6). It reuses D1's (recosi-0df.1) interface rather
 * than recomputing VAT independently — the per-market VAT rate and base gross are
 * read straight off the rendered base price element's DOM contract
 * (`.price-list[data-vat-rate|-price-current|-price-regular]`, snippets/price.liquid),
 * and the ex-VAT figure uses that snippet's exact integer formula so the two never
 * diverge. Money is formatted through window.theme.moneyFormat (the store format),
 * so currency + separators match the server-rendered price per market.
 *
 * D2 (recosi-0df.2) Omnibus: the 30-day-low line is the BASE product's value and is
 * rendered verbatim by price.liquid — the engine does NOT recompute or touch it for
 * a configured total (spec §6).
 *
 * SCOPE: price only. Stock/compatibility gating is E5 (recosi-qzf.5); the nested
 * cart add is E6 (recosi-qzf.6). IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  // E4 label fallbacks — used only if the labels island is missing/unparseable
  // (E3 merges the island onto the element as `.labels`; these fill any gap).
  var PRICE_LABELS = {
    total_label: 'Configured total',
    free: 'Free',
    tax_excluded_html: '%%PRICE%% excl. VAT',
    regular_price: 'Regular price'
  };

  function elem(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function labelsFor(configurator) {
    return Object.assign({}, PRICE_LABELS, configurator.labels || {});
  }

  // --- money: standard Shopify formatMoney against the store's money format ---
  function activeFormat() {
    var t = window.theme || {};
    if (t.currencyCodeEnabled && t.moneyWithCurrencyFormat) return t.moneyWithCurrencyFormat;
    return t.moneyFormat || '${{amount}}';
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (thousands == null) thousands = ',';
    if (decimal == null) decimal = '.';
    if (isNaN(cents) || cents == null) return '0';
    var amount = (cents / 100).toFixed(precision);
    var parts = amount.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    var value = '';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    if (!match) return format;
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ''); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function money(cents) { return formatMoney(cents, activeFormat()); }

  // Read the base price + per-market VAT off D1's DOM contract, scoped to this
  // product form. Falls back to the island base price (VAT/regular then unknown).
  function readBase(configurator) {
    var scope = configurator.closest('product-form') || document;
    var priceEl = scope.querySelector('.price-list[data-price]');
    var islandPrice = (configurator.data && configurator.data.base) ? configurator.data.base.price : null;
    if (!priceEl) {
      return { current: islandPrice != null ? islandPrice : null, regular: null, vatRate: 0, showExVat: false };
    }
    function intAttr(name) {
      var v = priceEl.getAttribute(name);
      return v == null || v === '' ? null : parseInt(v, 10);
    }
    var current = intAttr('data-price-current');
    return {
      current: current != null ? current : islandPrice,
      regular: intAttr('data-price-regular'),
      // New-equivalent tier (custom.original_new_price). Mirrors price.liquid:
      // a total below this still reads as a deal even with no compare_at.
      newEquiv: intAttr('data-price-new'),
      vatRate: intAttr('data-vat-rate') || 0,
      // Mirror settings.show_ex_vat as actually rendered by D1 for this market.
      showExVat: !!(priceEl.parentNode && priceEl.parentNode.querySelector('.price__ex-vat'))
    };
  }

  // Fill the static per-option add cost. Runs once the configurator is ready and
  // again after a <product-rerender> swap re-upgrades the element.
  function fillOptionPrices(configurator, labels) {
    var options = configurator.querySelectorAll('[data-configurator-option]');
    Array.prototype.forEach.call(options, function (option) {
      var slot = option.querySelector('[data-configurator-price]');
      if (!slot) return;
      var priceAttr = option.getAttribute('data-price');
      // "Keep standard" and any option without a price add nothing.
      if (option.hasAttribute('data-configurator-none') || priceAttr == null) {
        slot.textContent = '';
        slot.hidden = true;
        return;
      }
      var price = parseInt(priceAttr, 10);
      var compareAttr = option.getAttribute('data-compare-at');
      var compareAt = compareAttr == null || compareAttr === '' ? null : parseInt(compareAttr, 10);

      slot.textContent = '';
      if (price === 0) {
        var free = elem('span', 'configurator-option__price-free');
        free.textContent = labels.free;
        slot.appendChild(free);
      } else {
        if (compareAt != null && compareAt > price) {
          var cmp = elem('span', 'configurator-option__price-compare');
          cmp.textContent = money(compareAt);
          slot.appendChild(cmp);
        }
        var add = document.createElement('span');
        // Closed up as the design writes it: "+89,00 €", not "+ 89,00 €". The NBSP this
        // replaces was doing a real job — keeping the plus with its amount — so that job
        // moved to `white-space: nowrap` on .configurator-option__price rather than being
        // dropped.
        add.textContent = '+' + money(price);
        slot.appendChild(add);
      }
      slot.hidden = false;
    });
  }

  // Recompute + render the configured total. base + Σ selected components (additive).
  function renderTotal(configurator, detail, labels) {
    var slot = configurator.querySelector('[data-configurator-total]');
    if (!slot) return;

    var base = readBase(configurator);
    if (base.current == null) { slot.hidden = true; return; } // nothing to total against

    var components = (detail && detail.selectedComponents) || [];
    var totalCurrent = base.current;
    components.forEach(function (c) { totalCurrent += (c.price || 0); });

    var label = elem('span', 'configurator__total-label');
    label.textContent = labels.total_label;

    var prices = elem('span', 'configurator__total-prices');

    // ONE NUMBER: the amount payable for the configuration as shown. The struck regular
    // price and the "brez DDV" line were both rendered here until 2026-09-10 and were cut
    // at the client's review to match the design.
    //
    // Nothing is lost by cutting them, which is why it was an easy call: the base price
    // block a few lines above this card already shows the struck compare-at tier, the
    // ex-VAT figure and the Omnibus line for the same product. Repeating them against a
    // configured total meant three prices stacked under a heading that promises one, and
    // the one the shopper is looking for was the smallest of them.
    //
    // The struck-price, highlight and ex-VAT machinery that fed those three lines was
    // deleted with them rather than left computed-but-unused. `readBase()` still reports
    // `regular`, `newEquiv`, `vatRate` and `showExVat`, so restoring any of it is small —
    // the ex-VAT formula to copy back is in snippets/price.liquid, which is where it has to
    // match anyway.
    var current = elem('span', 'price configurator__total-amount');
    current.setAttribute('data-configurator-total-price', totalCurrent);
    current.appendChild(document.createTextNode(money(totalCurrent)));
    prices.appendChild(current);

    slot.textContent = '';
    slot.appendChild(label);
    slot.appendChild(prices);
    slot.hidden = false;

    patchBasePrice(configurator, totalCurrent - base.current, base, labels);
    // Leanpay's product widget follows the configured total, see docs/leanpay-gls.md.
    window.dispatchEvent(new CustomEvent('leanpay:priceChange', { detail: { price: totalCurrent / 100 } }));
  }

  // The amount is the node's last non-empty text node, which is how price.liquid emits it.
  function setMoneyText(node, cents) {
    for (var i = node.childNodes.length - 1; i >= 0; i--) {
      var child = node.childNodes[i];
      if (child.nodeType === 3 && child.nodeValue.trim() !== '') {
        child.nodeValue = money(cents);
        return;
      }
    }
    node.appendChild(document.createTextNode(money(cents)));
  }

  // Sum the selected upgrades into the price at the top of the page. What moves, what does
  // not and why: docs/PR-fix-pdp-client-review-2026-09.md.
  function patchBasePrice(configurator, delta, base, labels) {
    var scope = configurator.closest('product-form') || document;
    var priceEl = scope.querySelector('.price-list[data-price]');
    if (!priceEl || base.current == null) return;

    var amount = priceEl.querySelector('.price:not(.price--compare):not(.price--new-equivalent)');
    if (amount) setMoneyText(amount, base.current + delta);

    if (base.regular != null) {
      var compare = priceEl.querySelector('.price--compare .price__amount');
      if (compare) compare.textContent = money(base.regular + delta);
    }

    var exVat = priceEl.parentNode && priceEl.parentNode.querySelector('.price__ex-vat');
    if (exVat && base.showExVat && base.vatRate > 0 && labels.tax_excluded_html) {
      // price.liquid's integer formula, copied so the two can never round apart.
      var divisor = 100 + base.vatRate;
      var cents = Math.floor(((base.current + delta) * 100 + Math.floor(divisor / 2)) / divisor);
      exVat.setAttribute('data-price-ex-vat', cents);
      exVat.textContent = labels.tax_excluded_html.replace('%%PRICE%%', money(cents));
    }

    priceEl.setAttribute('data-configurator-delta', delta);
  }

  function onReady(event) {
    var configurator = event.detail.configurator;
    var labels = labelsFor(configurator);
    fillOptionPrices(configurator, labels);
    renderTotal(configurator, event.detail, labels);
  }

  function onChange(event) {
    var configurator = event.detail.configurator;
    renderTotal(configurator, event.detail, labelsFor(configurator));
  }

  document.addEventListener('configurator:ready', onReady);
  document.addEventListener('configurator:change', onChange);

  // Order-independence: E3 may have upgraded (and emitted configurator:ready)
  // before these listeners attached. Render any configurator already in the ready
  // state via its public getState().
  function sweep() {
    var forms = document.querySelectorAll('configurator-form');
    Array.prototype.forEach.call(forms, function (configurator) {
      if (typeof configurator.getState !== 'function') return; // not upgraded yet → listener will catch it
      var labels = labelsFor(configurator);
      fillOptionPrices(configurator, labels);
      renderTotal(configurator, configurator.getState(), labels);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/**
 * PDP spec sentence AND chips follow the configuration (client, items 16 + 2026-09-19).
 * DISPLAY ONLY; the cart is E6's. Parsing rules + limits:
 * docs/PR-fix-pdp-client-review-2026-09.md.
 */
(function () {
  'use strict';

  // The three dimensions the sentence carries. Warranty is in the strip, not here.
  var SYNC_KEYS = ['ram', 'ssd', 'os'];

  function gigabytes(text) {
    var m = String(text || '').match(/(\d+(?:[.,]\d+)?)\s*(GB|TB)/i);
    if (!m) return null;
    var n = parseFloat(m[1].replace(',', '.'));
    return m[2].toUpperCase() === 'TB' ? n * 1024 : n;
  }

  function formatSize(gb) {
    if (gb >= 1024 && gb % 1024 === 0) return (gb / 1024) + ' TB';
    return gb + ' GB';
  }

  // "+8 GB" is added to the base; "1TB SSD" is already the result. No size -> null.
  function upgradedSize(componentTitle, baseText) {
    var plus = String(componentTitle).match(/\+\s*(\d+(?:[.,]\d+)?)\s*(GB|TB)/i);
    var baseGb = gigabytes(baseText);
    if (plus && baseGb != null) {
      var add = parseFloat(plus[1].replace(',', '.'));
      if (plus[2].toUpperCase() === 'TB') add *= 1024;
      return formatSize(baseGb + add);
    }
    var abs = gigabytes(componentTitle);
    return abs == null ? null : formatSize(abs);
  }

  function valueFor(key, component, baseText) {
    if (key === 'os') return component.title;
    var size = upgradedSize(component.title, baseText);
    if (!size) return null;
    return key === 'ram' ? size + ' RAM' : size + ' SSD';
  }

  // The chips under the title read the same four metafields the sentence does, so leaving
  // them on the base value contradicts it: "32 GB RAM" in the sentence, "8 GB" in the chip.
  function syncChips(detail) {
    var specs = (detail.base && detail.base.specs) || {};
    ['ram', 'ssd'].forEach(function (key) {
      var chip = document.querySelector('[data-product-chips] .product-chip[data-spec="' + key + '"] .product-chip__value');
      if (!chip) return;
      if (chip.dataset.chipBase == null) chip.dataset.chipBase = chip.textContent;
      var component = detail.selections ? detail.selections[key] : null;
      var size = component ? upgradedSize(component.title, specs[key]) : null;
      // The RAM chip keeps its memory type ("32 GB DDR4"); the SSD chip names the part.
      var ramType = chip.dataset.chipBase.replace(/^\s*[\d.,]+\s*(GB|TB)\s*/i, '').trim();
      var upgraded = key === 'ssd' ? size + ' SSD' : (ramType ? size + ' ' + ramType : size);
      chip.textContent = size ? upgraded : chip.dataset.chipBase;
    });
  }

  function sync(configurator, detail) {
    syncChips(detail);

    var node = document.querySelector('[data-product-subline]');
    if (!node) return;
    if (node.dataset.sublineBase == null) node.dataset.sublineBase = node.textContent;

    var specs = (detail.base && detail.base.specs) || {};
    var parts = node.dataset.sublineBase.split(' · ');

    SYNC_KEYS.forEach(function (key) {
      var component = detail.selections ? detail.selections[key] : null;
      var baseText = specs[key];
      if (!component || !baseText) return;
      var replacement = valueFor(key, component, baseText);
      if (!replacement) return;
      // No match = the sentence does not carry this dimension; never invent a segment.
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf(baseText) !== -1) { parts[i] = replacement; break; }
      }
    });

    node.textContent = parts.join(' · ');
  }

  document.addEventListener('configurator:ready', function (event) {
    sync(event.detail.configurator, event.detail);
  });
  document.addEventListener('configurator:change', function (event) {
    sync(event.detail.configurator, event.detail);
  });

  // The element upgrades (and emits ready) before this listener attaches. Same sweep as E4.
  function sweep() {
    var forms = document.querySelectorAll('configurator-form');
    Array.prototype.forEach.call(forms, function (configurator) {
      if (typeof configurator.getState !== 'function') return;
      sync(configurator, configurator.getState());
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sweep);
  else sweep();
})();

/**
 * ------------------------------------------------------------
 * PDP configurator STOCK + COMPATIBILITY ENGINE — E5 (recosi-qzf.5)
 *
 * Subscribes to the E3 <configurator-form> selection events and fills the empty
 * per-option stock hook E3 leaves behind, plus enforces buildability:
 *   [data-configurator-stock]   per upgrade option — In stock / "only N left" /
 *                               Out of stock badge, driven by the live variant
 *                               inventory E2 serialized (data-available / -qty /
 *                               -stock-bearing on the option label).
 *
 * Compatibility here is MEMBERSHIP: the option set already IS the Base PC's
 * `compatible_upgrades` list (E2), so an incompatible Component is never offered.
 * What remains is availability: a tracked Component with no stock is disabled +
 * messaged so it can never enter a Configuration ("keep standard" stays selectable,
 * so the Base PC is still configurable). Untracked service/licence Components
 * (stockBearing=false — OS, warranty) are always available and carry no badge.
 *
 * Every change re-validates the whole selection and publishes buildability so the
 * cart add (E6/recosi-qzf.6) can gate on it without re-reading the DOM:
 *   - `data-configurator-valid="true|false"` on the mount
 *   - [data-configurator-stock-warning] in the summary (shown only when invalid)
 *   - configurator:validity  detail = { configurator, valid, unavailable[] }
 *
 * Availability is a page-load snapshot — the theme is a read-only data consumer
 * (ADR-0003), so there is no live restock/sellout poll; the definitive stock check
 * is server-side on E6's /cart/add.js. This engine keeps an unbuildable config from
 * ever being submitted and surfaces it if E6 (or future live data) reports one.
 *
 * SCOPE: stock display + availability gating + validity signal. Price is E4
 * (recosi-qzf.4); the nested cart add + button wiring is E6 (recosi-qzf.6).
 * IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  // Show "only N left" once tracked stock is at/under this. No global theme setting
  // exists for the configurator (the PDP threshold is a per-block variant-selector
  // setting); this is a sensible urgency cutoff for build-to-order components.
  var LOW_STOCK_THRESHOLD = 5;

  // E5 label fallbacks — used only if the labels island is missing/unparseable
  // (E3 merges the island onto the element as `.labels`; these fill any gap).
  var STOCK_LABELS = {
    stock_in: 'In stock',
    stock_low: 'Only %%COUNT%% left',
    stock_out: 'Out of stock',
    unavailable_notice: 'Some selected upgrades are no longer available. Please choose another option.'
  };

  function elem(tag, className) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    return node;
  }

  function labelsFor(configurator) {
    return Object.assign({}, STOCK_LABELS, configurator.labels || {});
  }

  function intAttr(el, name) {
    var v = el.getAttribute(name);
    return v == null || v === '' ? null : parseInt(v, 10);
  }

  // The single availability rule (spec §4.2): an untracked service/licence is always
  // available; a tracked component is available only when its variant is available AND
  // its snapshot qty is positive. Both the per-option badge (availabilityOf, reading the
  // serialized DOM attributes) and the build-validity check (validate, reading the
  // component objects) resolve through this one predicate, so the badge and the
  // configurator:validity signal can never drift apart.
  function isAvailable(available, stockBearing, qty) {
    if (available === false) return false;
    return !stockBearing || (qty != null && qty > 0);
  }

  // Availability of a single option label, from the attributes E2 serialized.
  function availabilityOf(option) {
    var tracked = option.getAttribute('data-stock-bearing') === 'true';
    var available = option.getAttribute('data-available') !== 'false';
    var qty = intAttr(option, 'data-qty');
    return { tracked: tracked, available: isAvailable(available, tracked, qty), qty: tracked ? qty : null };
  }

  // Fill the per-option stock badge + disable options that cannot be built. Runs
  // once the configurator is ready and again after a <product-rerender> swap
  // re-upgrades the element (E3 re-renders fresh options each time).
  function fillOptionStock(configurator, labels) {
    var options = configurator.querySelectorAll('[data-configurator-option]');
    Array.prototype.forEach.call(options, function (option) {
      var slot = option.querySelector('[data-configurator-stock]');
      var radio = option.querySelector('.configurator-option__radio');
      // "Keep standard" is always available and carries no stock state.
      if (option.hasAttribute('data-configurator-none')) {
        if (slot) { slot.textContent = ''; slot.hidden = true; }
        return;
      }

      var stock = availabilityOf(option);
      var unavailable = !stock.available;

      option.classList.toggle('configurator-option--unavailable', unavailable);
      if (unavailable) {
        option.setAttribute('aria-disabled', 'true');
      } else {
        option.removeAttribute('aria-disabled');
      }
      // Block selection of an out-of-stock option so it can never enter a build.
      if (radio) radio.disabled = unavailable;

      if (!slot) return;
      slot.textContent = '';
      slot.className = 'configurator-option__stock';

      // Untracked service/licence in stock → no badge (always available, no count).
      if (!stock.tracked && stock.available) {
        slot.hidden = true;
        return;
      }

      var badge;
      if (unavailable) {
        badge = labels.stock_out;
        slot.classList.add('configurator-option__stock--out');
      } else if (stock.qty != null && stock.qty <= LOW_STOCK_THRESHOLD) {
        badge = labels.stock_low.replace('%%COUNT%%', stock.qty);
        slot.classList.add('configurator-option__stock--low');
      } else {
        badge = labels.stock_in;
        slot.classList.add('configurator-option__stock--in');
      }
      slot.textContent = badge;
      slot.hidden = false;
    });
  }

  // The summary's mid-config warning region — created lazily (E3 renders no warning).
  function warningSlot(configurator) {
    var summary = configurator.querySelector('[data-configurator-summary]');
    if (!summary) return null;
    var slot = summary.querySelector('[data-configurator-stock-warning]');
    if (!slot) {
      slot = elem('p', 'configurator__stock-warning');
      slot.setAttribute('data-configurator-stock-warning', '');
      slot.setAttribute('role', 'alert');
      slot.hidden = true;
      summary.appendChild(slot);
    }
    return slot;
  }

  // Re-validate the whole selection: a build is valid unless a selected Component is
  // unavailable. Reflects buildability on the mount + summary and publishes it for E6.
  function validate(configurator, detail, labels) {
    var components = (detail && detail.selectedComponents) || [];
    var unavailable = components.filter(function (c) {
      return !isAvailable(c.available, c.stockBearing, c.qty);
    });
    var valid = unavailable.length === 0;

    configurator.setAttribute('data-configurator-valid', valid ? 'true' : 'false');

    var slot = warningSlot(configurator);
    if (slot) {
      if (valid) {
        slot.hidden = true;
        slot.textContent = '';
      } else {
        slot.textContent = labels.unavailable_notice;
        slot.hidden = false;
      }
    }

    configurator.dispatchEvent(new CustomEvent('configurator:validity', {
      bubbles: true,
      detail: { configurator: configurator, valid: valid, unavailable: unavailable }
    }));
  }

  function onReady(event) {
    var configurator = event.detail.configurator;
    var labels = labelsFor(configurator);
    fillOptionStock(configurator, labels);
    validate(configurator, event.detail, labels);
  }

  function onChange(event) {
    var configurator = event.detail.configurator;
    validate(configurator, event.detail, labelsFor(configurator));
  }

  document.addEventListener('configurator:ready', onReady);
  document.addEventListener('configurator:change', onChange);

  // Order-independence: E3 may have upgraded (and emitted configurator:ready) before
  // these listeners attached. Fill + validate any configurator already in the ready
  // state via its public getState() (mirrors the E4 sweep).
  function sweep() {
    var forms = document.querySelectorAll('configurator-form');
    Array.prototype.forEach.call(forms, function (configurator) {
      if (typeof configurator.getState !== 'function') return; // not upgraded yet → listener will catch it
      var labels = labelsFor(configurator);
      fillOptionStock(configurator, labels);
      validate(configurator, configurator.getState(), labels);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/**
 * ------------------------------------------------------------
 * PDP configurator NESTED CART ADD — E6 (recosi-qzf.6)
 *
 * Takes over the theme's add-to-cart for a configurable Base PC so the whole
 * Configuration lands in one cart write: one parent line (the Base PC) plus one
 * nested child line per selected upgrade, linked via the Ajax Cart API `parent_id`
 * (= the parent's VARIANT id, within the same POST — verified live end-to-end
 * through checkout by the E1 spike, recosi-qzf.1).
 *
 * The Base PC stays purchasable without JS via the standard buy button (progressive
 * enhancement — E2 detaches the configurator radios from the product form so a
 * no-JS submit adds base-only). When JS + a ready <configurator-form> are present,
 * this intercepts the buy-button click at the form (capture phase, so it runs before
 * the theme's own buy-button handler), suppresses the native base-only add, and POSTs
 * the nested payload itself. It then reuses the theme's own refresh path by dispatching
 * `product:added`, so the mini-cart re-renders + opens exactly as for a normal add.
 *
 * GATING: the add is blocked while E5 (recosi-qzf.5) reports the build unbuildable
 * (`data-configurator-valid="false"`) — E5 already surfaces which upgrade is gone.
 * The definitive stock check is still Shopify's on /cart/add.js; a 422 there is shown
 * inline (the server-localised reason when available, else product.configurator.add_error).
 *
 * EXPRESS CHECKOUT: the dynamic checkout button (Shop Pay "Buy it now" etc.) buys the
 * form's single variant and bypasses the cart, so it would silently drop every upgrade.
 * A configured product hides it (via .product-form--has-configurator, styled in theme.css).
 *
 * SCOPE: the nested add + its error/validity states. Cart-side grouping + integrity
 * (read-only components, cascade remove, edit) is the decorator IIFE below + the
 * server-side grouping in mini-cart.liquid / main-cart.liquid. IIFE-wrapped (Shopify
 * bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var FALLBACK_ADD_ERROR = "We couldn't add your configuration to the cart. Please try again.";

  function productForm(configurator) {
    var id = configurator.getAttribute('data-product-form');
    var byId = id && document.getElementById(id);
    return byId || configurator.closest('form[action*="/cart/add"]');
  }

  // Inline error region in the summary — created lazily (mirrors E5's warning slot).
  function errorSlot(configurator) {
    var summary = configurator.querySelector('[data-configurator-summary]');
    if (!summary) return null;
    var slot = summary.querySelector('[data-configurator-cart-error]');
    if (!slot) {
      slot = document.createElement('p');
      slot.className = 'configurator__cart-error';
      slot.setAttribute('data-configurator-cart-error', '');
      slot.setAttribute('role', 'alert');
      slot.hidden = true;
      summary.appendChild(slot);
    }
    return slot;
  }

  function showError(configurator, message) {
    var slot = errorSlot(configurator);
    if (!slot) return;
    var labels = configurator.labels || {};
    slot.textContent = message || labels.add_error || FALLBACK_ADD_ERROR;
    slot.hidden = false;
  }

  function clearError(configurator) {
    var slot = configurator.querySelector('[data-configurator-cart-error]');
    if (slot) { slot.hidden = true; slot.textContent = ''; }
  }

  function submitConfiguration(configurator, buyButton, clickedEl) {
    // E5 validity gate — E5 already renders the reason, so just refuse the add.
    if (configurator.getAttribute('data-configurator-valid') === 'false') return;
    if (typeof configurator.getState !== 'function') return;

    var state = configurator.getState();
    if (!state.base || state.base.variantId == null) return;

    var form = productForm(configurator);
    var qtyInput = form && form.querySelector('[name="quantity"]');
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!quantity || quantity < 1) quantity = 1;

    var parentVariantId = state.base.variantId;
    // One parent line + one nested child per selected upgrade. A build with no upgrades
    // is just the base line (identical to the native add). Quantity applies uniformly to
    // all nested lines; nested qty > 1 is honoured but not covered by the E1 spike (E7 QA).
    var parentLine = { id: parentVariantId, quantity: quantity };
    var komplet = state.base.komplet;
    if (komplet) {
      // A komplet's PC is the parent; its parts ride nested like upgrades. See docs/magento-join.md.
      parentLine.properties = { 'Komplet': komplet.title, '_komplet_url': komplet.url };
    }
    var items = [parentLine];
    if (komplet) {
      (komplet.included || []).forEach(function (part) {
        items.push({ id: part.variantId, quantity: quantity, parent_id: parentVariantId });
      });
    }
    state.selectedComponents.forEach(function (c) {
      items.push({ id: c.variantId, quantity: quantity, parent_id: parentVariantId });
    });

    var button = (clickedEl && clickedEl.closest('button')) || buyButton.querySelector('button');
    if (button) button.setAttribute('disabled', 'disabled');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    clearError(configurator);

    // Edit = remove + re-add (ADR-0001 / recosi-yey). When this PDP was opened from a cart
    // edit link, the ORIGINAL bundle must be zeroed BEFORE the re-add: re-posting the same
    // parent variant while its line still exists makes Shopify MERGE into it (parent qty
    // doubles) and append the new children alongside the stale ones. Removing first means the
    // add lands as one clean bundle at the requested quantity. On a normal add this resolves
    // immediately, so the add path is unchanged.
    removeOriginalBundle(configurator).then(function () {
      addConfiguration(configurator, buyButton, button, items);
    });
  }

  // Zero the original bundle's parent + child lines for an edit re-add. Resolves on any outcome
  // (a stale/absent key is a /cart/update.js no-op, so the re-add always proceeds) and consumes
  // the edit state, so a second click on the same page behaves as a normal add — never another
  // remove+merge cycle.
  function removeOriginalBundle(configurator) {
    var editState = configurator.editState;
    if (!editState || !editState.active) return Promise.resolve();
    configurator.editState = { active: false }; // one edit per link

    var updates = {};
    if (editState.parentKey) updates[editState.parentKey] = 0;
    editState.childKeys.forEach(function (key) { if (key) updates[key] = 0; });
    if (Object.keys(updates).length === 0) return Promise.resolve();

    return fetch(window.routes.cartUrl + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    }).then(function () {}).catch(function () {}); // best-effort; the add still runs
  }

  function addConfiguration(configurator, buyButton, button, items) {
    fetch(window.routes.cartAddUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (button) button.removeAttribute('disabled');
      if (!response.ok) {
        response.json().then(function (content) {
          showError(configurator, content && content.description);
        }).catch(function () { showError(configurator, null); });
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        return;
      }
      // Reuse the theme's refresh path. total quantity is transient — _rerender reads the
      // authoritative item_count off the re-fetched section HTML.
      var totalQuantity = items.reduce(function (n, it) { return n + it.quantity; }, 0);
      buyButton.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { button: button || buyButton, variant: null, quantity: totalQuantity, productAdded: true }
      }));
      // On a full cart page there is no drawer to open — the theme redirects to the cart
      // after a native add, so mirror that.
      if (window.theme && (window.theme.pageType === 'cart' || window.theme.cartType === 'page')) {
        window.location.href = window.routes.cartUrl;
      }
    }).catch(function () {
      if (button) button.removeAttribute('disabled');
      showError(configurator, null);
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    });
  }

  // Wire a product form once. The listener looks up the *current* configurator at click
  // time (not a captured reference) so it survives a <product-rerender> variant swap that
  // replaces the configurator instance. Capture phase = runs before the theme buy-button.
  function wire(configurator) {
    var form = productForm(configurator);
    if (!form) return;
    form.classList.add('product-form--has-configurator'); // CSS hides express checkout
    // Edit mode: relabel the buy button so the shopper knows this REPLACES their cart bundle
    // rather than adding a second one (recosi-yey). Silent no-op if the label is unset.
    if (configurator.editState && configurator.editState.active) {
      form.classList.add('product-form--configurator-editing');
      var editButton = form.querySelector('.product-form__add-button');
      var updateLabel = (configurator.labels || {}).update_button;
      if (editButton && updateLabel) editButton.textContent = updateLabel;
    }
    if (form._configuratorAddWired) return;
    form._configuratorAddWired = true;
    form.addEventListener('click', function (event) {
      // Only the add-to-cart submit lives inside <buy-button>; the payment button does not,
      // so this never intercepts express checkout, variant pickers, or configurator radios.
      var buyButton = event.target.closest('buy-button');
      if (!buyButton) return;
      var cfg = form.querySelector('configurator-form');
      if (!cfg || !cfg._ready) return; // not a configured product (or not ready) → native add
      event.preventDefault();
      event.stopImmediatePropagation();
      submitConfiguration(cfg, buyButton, event.target);
    }, true);
  }

  document.addEventListener('configurator:ready', function (event) { wire(event.detail.configurator); });

  // Order-independence: a configurator may have emitted `configurator:ready` before this
  // listener attached (mirrors the E4/E5 sweeps).
  function sweepAdd() {
    var forms = document.querySelectorAll('configurator-form');
    Array.prototype.forEach.call(forms, function (configurator) {
      if (configurator._ready) wire(configurator);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweepAdd);
  } else {
    sweepAdd();
  }
})();

/**
 * ------------------------------------------------------------
 * CONFIGURED-CART INTEGRITY — E6 (recosi-qzf.6)
 *
 * A Configuration is atomic: the Base PC parent line plus its nested children.
 * Remove cascades and quantity scales the whole build — both must be JS, because
 * Shopify treats nested lines as independent. See docs/liquid-gotchas.md.
 *
 * IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  function cascadeRemove(control) {
    var updates = {};
    var parentKey = control.getAttribute('data-line-item-key');
    if (parentKey) updates[parentKey] = 0;
    (control.getAttribute('data-child-keys') || '').split(',').forEach(function (key) {
      if (key) updates[key] = 0;
    });
    if (Object.keys(updates).length === 0) return;

    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    fetch(window.routes.cartUrl + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    }).then(function () {
      if (window.theme && window.theme.pageType === 'cart') {
        window.location.reload();
      } else {
        // Reuse the mini-cart's own refresh (fetches the section HTML → re-runs Liquid).
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }).catch(function () {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    });
  }

  // "key=units,…" → [{key, units}]. Units is the component's per-build quantity.
  function parseChildUnits(raw) {
    return (raw || '').split(',').reduce(function (list, pair) {
      var at = pair.lastIndexOf('=');
      if (at < 1) return list;
      var units = parseInt(pair.slice(at + 1), 10);
      list.push({ key: pair.slice(0, at), units: units > 0 ? units : 1 });
      return list;
    }, []);
  }

  function cartUpdate(updates) {
    return fetch(window.routes.cartUrl + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) { return response.json(); });
  }

  function refreshCart() {
    if (window.theme && window.theme.pageType === 'cart') {
      window.location.reload();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  // Scales the whole build, then re-reads the response because update.js clamps
  // silently — whatever every line granted decides the real build count.
  function cascadeQuantity(selector, quantity) {
    var parentKey = selector.getAttribute('data-line-item-key');
    var children = parseChildUnits(selector.getAttribute('data-child-units'));
    if (!parentKey) return;

    var updates = {};
    updates[parentKey] = quantity;
    children.forEach(function (child) { updates[child.key] = child.units * quantity; });

    selector.setAttribute('aria-busy', 'true');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    cartUpdate(updates).then(function (cart) {
      var byKey = {};
      (cart.items || []).forEach(function (item) { byKey[item.key] = item.quantity; });

      var granted = byKey[parentKey] === undefined ? quantity : byKey[parentKey];
      children.forEach(function (child) {
        var have = byKey[child.key];
        if (have === undefined) return;
        granted = Math.min(granted, Math.floor(have / child.units));
      });

      if (granted === quantity || granted < 1) {
        refreshCart();
        return;
      }

      var corrected = {};
      corrected[parentKey] = granted;
      children.forEach(function (child) { corrected[child.key] = child.units * granted; });
      return cartUpdate(corrected).then(refreshCart);
    }).catch(function () {
      selector.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    });
  }

  function clamp(selector, quantity) {
    var max = parseInt(selector.getAttribute('data-max'), 10);
    if (quantity < 1) return 1;
    if (max > 0 && quantity > max) return max;
    return quantity;
  }

  // Delegated on document so it survives every cart re-render without re-binding. These
  // controls carry no data-action, so the theme's own quantity handler ignores them.
  document.addEventListener('click', function (event) {
    var control = event.target.closest('[data-configurator-cascade-remove]');
    if (control) {
      event.preventDefault();
      cascadeRemove(control);
      return;
    }

    var step = event.target.closest('[data-configurator-qty-step]');
    if (!step || step.hasAttribute('disabled')) return;
    var selector = step.closest('[data-configurator-qty]');
    if (!selector) return;
    event.preventDefault();

    var input = selector.querySelector('[data-configurator-qty-input]');
    var current = parseInt(input && input.value, 10) || 1;
    var next = clamp(selector, current + parseInt(step.getAttribute('data-configurator-qty-step'), 10));
    if (next === current) return;
    if (input) input.value = next;
    cascadeQuantity(selector, next);
  });

  // The cart page is one big <form>, so Enter would submit it without applying anything.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.matches('[data-configurator-qty-input]')) return;
    event.preventDefault();
    event.target.blur();
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-configurator-qty-input]');
    if (!input) return;
    var selector = input.closest('[data-configurator-qty]');
    if (!selector) return;

    var next = clamp(selector, parseInt(input.value, 10) || 1);
    input.value = next;
    cascadeQuantity(selector, next);
  });
})();

/**
 * ------------------------------------------------------------
 * PRODUCT COMPARE — C7 (recosi-hrn.6)
 *
 * Select-to-compare for Base PCs. A per-card "Compare" checkbox
 * (snippets/product-compare-toggle.liquid) feeds a single <product-compare> mount
 * (snippets/product-compare-bar.liquid) rendered once on collection + search. This
 * element owns:
 *   - the floating selection BAR (thumbnails + count + Compare/Clear), and
 *   - the compare DRAWER (an accessible dialog holding the comparison table).
 *
 * STATE lives in localStorage keyed by market (SLO/HR) — only the product ids +
 * minimal bar display data (title/url/thumbnail), never price/spec snapshots. On
 * "Compare" the TABLE is rendered fresh server-side by sections/product-compare.liquid
 * via the Section Rendering API (routes.searchUrl + `id:` query — the theme's own
 * recently-viewed pattern), so the D1 price module + spec metafields render per market
 * and the theme stays a read-only data consumer (ADR-0003). No client-side VAT/currency.
 *
 * Defined as a custom element so it re-upgrades after any DOM swap. Toggles are handled
 * by delegation + a MutationObserver, so filter/facet re-renders of the product grid
 * (theme replaces .collection__dynamic-part) keep the checkboxes in sync with the
 * stored selection. IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var ELEMENT_NAME = 'product-compare';
  if (window.customElements && customElements.get(ELEMENT_NAME)) return;

  var STORE_KEY = 'recosi:compare';

  var FALLBACK_LABELS = {
    bar_title: 'Compare',
    compare: 'Compare',
    clear: 'Clear',
    remove: 'Remove',
    max_reached: 'You can compare up to that many products.',
    drawer_title: 'Compare products',
    close: 'Close',
    empty: 'Select products to compare.',
    loading: 'Loading…',
    error: 'Could not load the comparison. Please try again.'
  };

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    return node;
  }

  function parseIsland(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  function readStore() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)); } catch (e) { return null; }
  }

  function writeStore(value) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(value)); } catch (e) { /* private mode / quota */ }
  }

  function toggleData(toggle) {
    return {
      id: toggle.getAttribute('data-product-id'),
      title: toggle.getAttribute('data-product-title') || '',
      url: toggle.getAttribute('data-product-url') || '',
      image: toggle.getAttribute('data-product-image') || ''
    };
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class ProductCompare extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.labels = Object.assign({}, FALLBACK_LABELS, parseIsland(this, 'script[data-compare-labels]') || {});
      this.max = parseInt(this.getAttribute('data-max'), 10) || 4;
      this.market = this.getAttribute('data-market') || '';
      this.sectionId = this.getAttribute('data-section-id') || 'product-compare';
      this.items = this._load();

      this._buildBar();
      this._buildDrawer();
      this._bindEvents();
      this._observeGrid();
      this._syncToggles();
      this._renderBar();
    }

    // --- persistence (market-keyed: switching SLO<->HR starts a fresh selection) ---
    _load() {
      var stored = readStore();
      if (!stored || stored.market !== this.market || !Array.isArray(stored.items)) return [];
      return stored.items.filter(function (item) { return item && item.id; });
    }

    _save() {
      writeStore({ market: this.market, items: this.items });
    }

    _indexOf(id) {
      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].id === id) return i;
      }
      return -1;
    }

    // --- mutations ---
    _add(data) {
      if (!data.id || this._indexOf(data.id) !== -1) return true;
      if (this.items.length >= this.max) { this._flashMax(); return false; }
      this.items.push(data);
      this._save();
      this._afterChange();
      return true;
    }

    _remove(id) {
      var index = this._indexOf(id);
      if (index === -1) return;
      this.items.splice(index, 1);
      this._save();
      this._afterChange();
      // A comparison needs at least two columns; drop out of the drawer otherwise.
      if (this._drawerOpen) {
        if (this.items.length < 2) { this._closeDrawer(); }
        else { this._loadTable(); }
      }
    }

    _clear() {
      this.items = [];
      this._save();
      this._afterChange();
      if (this._drawerOpen) this._closeDrawer();
    }

    _afterChange() {
      this._renderBar();
      this._syncToggles();
    }

    // --- toggle sync (checkbox state + cap-disable), rerun after grid re-renders ---
    _syncToggles() {
      var atCap = this.items.length >= this.max;
      var toggles = document.querySelectorAll('[data-compare-toggle]');
      var self = this;
      Array.prototype.forEach.call(toggles, function (toggle) {
        var checked = self._indexOf(toggle.getAttribute('data-product-id')) !== -1;
        if (toggle.checked !== checked) toggle.checked = checked;
        toggle.disabled = atCap && !checked;
      });
    }

    // --- floating selection bar ---
    _buildBar() {
      var bar = el('div', 'product-compare__bar', { hidden: '', role: 'region', 'aria-label': this.labels.bar_title });

      var title = el('span', 'product-compare__bar-title text--strong');
      title.textContent = this.labels.bar_title;
      bar.appendChild(title);

      this._thumbs = el('div', 'product-compare__thumbs');
      bar.appendChild(this._thumbs);

      var actions = el('div', 'product-compare__actions');

      this._compareBtn = el('button', 'product-compare__compare button button--small button--primary', { type: 'button' });
      this._compareBtn.addEventListener('click', this._openDrawer.bind(this));
      actions.appendChild(this._compareBtn);

      var clearBtn = el('button', 'product-compare__clear button button--small button--ternary', { type: 'button' });
      clearBtn.textContent = this.labels.clear;
      clearBtn.addEventListener('click', this._clear.bind(this));
      actions.appendChild(clearBtn);

      bar.appendChild(actions);

      this._message = el('p', 'product-compare__message', { role: 'status', 'aria-live': 'polite', hidden: '' });
      bar.appendChild(this._message);

      this._bar = bar;
      this.appendChild(bar);
    }

    _renderBar() {
      this._bar.hidden = this.items.length === 0;
      this._compareBtn.textContent = this.labels.compare + ' (' + this.items.length + ')';
      this._compareBtn.disabled = this.items.length < 2;

      this._thumbs.textContent = '';
      var self = this;
      this.items.forEach(function (item) {
        var thumb = el('div', 'product-compare__thumb');

        var link = el('a', 'product-compare__thumb-link', { href: item.url });
        if (item.image) {
          var img = el('img', 'product-compare__thumb-image', { src: item.image, alt: '', loading: 'lazy' });
          link.appendChild(img);
        }
        link.title = item.title;
        thumb.appendChild(link);

        var remove = el('button', 'product-compare__thumb-remove', { type: 'button', 'aria-label': self.labels.remove + ': ' + item.title });
        remove.innerHTML = '&times;';
        remove.addEventListener('click', function () { self._remove(item.id); });
        thumb.appendChild(remove);

        self._thumbs.appendChild(thumb);
      });
    }

    _flashMax() {
      if (!this._message) return;
      this._message.textContent = this.labels.max_reached;
      this._message.hidden = false;
      if (this._messageTimer) clearTimeout(this._messageTimer);
      var msg = this._message;
      this._messageTimer = setTimeout(function () { msg.hidden = true; msg.textContent = ''; }, 4000);
    }

    // --- compare drawer (accessible dialog) ---
    _buildDrawer() {
      var backdrop = el('div', 'product-compare__backdrop', { hidden: '' });
      backdrop.addEventListener('click', this._closeDrawer.bind(this));

      var drawer = el('div', 'product-compare__drawer', {
        hidden: '', role: 'dialog', 'aria-modal': 'true', 'aria-label': this.labels.drawer_title
      });

      var header = el('div', 'product-compare__drawer-head');
      var heading = el('h2', 'product-compare__drawer-title heading h4');
      heading.textContent = this.labels.drawer_title;
      header.appendChild(heading);

      var close = el('button', 'product-compare__drawer-close', { type: 'button', 'aria-label': this.labels.close });
      close.innerHTML = '&times;';
      close.addEventListener('click', this._closeDrawer.bind(this));
      header.appendChild(close);
      drawer.appendChild(header);

      this._tableSlot = el('div', 'product-compare__drawer-body', { 'data-compare-table-slot': '' });
      drawer.appendChild(this._tableSlot);

      this._backdrop = backdrop;
      this._drawer = drawer;
      this._closeButton = close;
      this.appendChild(backdrop);
      this.appendChild(drawer);
    }

    _openDrawer() {
      if (this.items.length < 2) return;
      this._drawerOpen = true;
      this._lastFocus = document.activeElement;
      this._backdrop.hidden = false;
      this._drawer.hidden = false;
      document.documentElement.classList.add('product-compare-open');
      this._closeButton.focus();
      this._loadTable();
    }

    _closeDrawer() {
      if (!this._drawerOpen) return;
      this._drawerOpen = false;
      this._backdrop.hidden = true;
      this._drawer.hidden = true;
      document.documentElement.classList.remove('product-compare-open');
      if (this._lastFocus && typeof this._lastFocus.focus === 'function') this._lastFocus.focus();
    }

    _loadTable() {
      var slot = this._tableSlot;
      slot.innerHTML = '<p class="product-compare__loading">' + this.labels.loading + '</p>';

      var query = this.items.map(function (item) { return 'id:' + item.id; }).join(' OR ');
      var base = (window.routes && window.routes.searchUrl) || '/search';
      var url = base + '?section_id=' + encodeURIComponent(this.sectionId) + '&type=product&q=' + encodeURIComponent(query);

      var self = this;
      fetch(url, { credentials: 'same-origin', method: 'GET' }).then(function (response) {
        if (!response.ok) throw new Error('compare fetch failed');
        return response.text();
      }).then(function (content) {
        if (!self._drawerOpen) return;
        var doc = new DOMParser().parseFromString(content, 'text/html');
        var section = doc.querySelector('[data-compare-section]');
        slot.innerHTML = section ? section.innerHTML : '<p class="product-compare__error">' + self.labels.error + '</p>';
      }).catch(function () {
        if (!self._drawerOpen) return;
        slot.innerHTML = '<p class="product-compare__error">' + self.labels.error + '</p>';
      });
    }

    // --- events ---
    _bindEvents() {
      var self = this;

      // Card checkboxes (delegated — survives grid re-render).
      this._onToggleChange = function (event) {
        var toggle = event.target.closest('[data-compare-toggle]');
        if (!toggle) return;
        if (toggle.checked) {
          if (!self._add(toggleData(toggle))) toggle.checked = false; // reverted at cap
        } else {
          self._remove(toggle.getAttribute('data-product-id'));
        }
      };
      document.addEventListener('change', this._onToggleChange);

      // Remove-a-column from inside the rendered table.
      this._onDocClick = function (event) {
        var remove = event.target.closest('[data-compare-remove]');
        if (!remove || !self.contains(remove)) return;
        event.preventDefault();
        self._remove(remove.getAttribute('data-product-id'));
      };
      document.addEventListener('click', this._onDocClick);

      // Esc to close + a minimal focus trap while the drawer is open.
      this._onKeydown = function (event) {
        if (!self._drawerOpen) return;
        if (event.key === 'Escape') { event.preventDefault(); self._closeDrawer(); return; }
        if (event.key !== 'Tab') return;
        var focusable = self._drawer.querySelectorAll(FOCUSABLE);
        if (!focusable.length) return;
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', this._onKeydown);

      // Cross-tab: reflect a selection change made in another tab.
      this._onStorage = function (event) {
        if (event.key !== STORE_KEY) return;
        self.items = self._load();
        self._renderBar();
        self._syncToggles();
        if (self._drawerOpen) {
          if (self.items.length < 2) self._closeDrawer();
          else self._loadTable();
        }
      };
      window.addEventListener('storage', this._onStorage);
    }

    // Re-sync checkboxes after the collection/search grid is swapped by filtering.
    _observeGrid() {
      var container = document.querySelector('.collection__dynamic-part') || document.querySelector('.product-list');
      if (!container || !window.MutationObserver) return;
      var self = this;
      this._observer = new MutationObserver(function () {
        if (self._syncQueued) return;
        self._syncQueued = true;
        (window.requestAnimationFrame || window.setTimeout)(function () {
          self._syncQueued = false;
          self._syncToggles();
        });
      });
      this._observer.observe(container, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onToggleChange);
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('storage', this._onStorage);
      if (this._observer) this._observer.disconnect();
      document.documentElement.classList.remove('product-compare-open');
    }
  }

  customElements.define(ELEMENT_NAME, ProductCompare);
})();

/**
 * ------------------------------------------------------------
 * Discount countdown — recosi-3pu
 *
 * Drives the live "ends in Nd HH:MM:SS" timer on the discount countdown badge
 * ([data-discount-countdown] with data-countdown-end = deadline in epoch SECONDS)
 * that snippets/product-badges.liquid renders on cards + PDP. One shared 1s interval
 * ticks every timer on the page (a collection can show many), and stops itself once
 * none remain.
 *
 * When a timer reaches zero it strips the discount framing from its product — the
 * countdown badge, the sibling --on-sale discount badge, and (on cards) the struck
 * .price--compare node — so a page left open past the deadline matches what the server
 * would now render. The theme is a READ-ONLY price consumer (ADR-0003): this only stops
 * ADVERTISING the sale. The amount actually charged is Shopify's (variant.price), which
 * the merchant reverts server-side at the deadline; nothing here touches checkout.
 *
 * TWO OUTPUT SHAPES, one clock. A card badge carries a single [data-countdown-time] node
 * and gets "Nd HH:MM:SS". The PDP bar (snippets/product-countdown.liquid, client design
 * 2026-09-08) carries [data-countdown-segments] and four [data-countdown-part] nodes and
 * gets a zero-padded number each. Both are found by the same selector and ticked in the
 * same pass, which is the point: two timers on one page eventually disagree, and the one
 * a customer notices is whichever outlives the offer.
 *
 * The initial value is server-rendered (no flash of empty), so if JS never runs the
 * badge still shows a correct, if frozen, countdown. IIFE-wrapped (Shopify bundles
 * theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var SELECTOR = '[data-discount-countdown]';
  var timer = null;

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  // Match the server format in product-card-countdown.liquid: "3 dni HH:MM:SS" (days dropped < 1d).
  // Day words come from data-countdown-days as one|two|few|other, picked by the page language.
  var PLURAL_INDEX = { one: 0, two: 1, few: 2, other: 3 };
  function dayWord(badge, days) {
    var forms = (badge.getAttribute('data-countdown-days') || 'd|d|d|d').split('|');
    var category = 'other';
    try {
      category = new Intl.PluralRules(document.documentElement.lang || 'sl').select(days);
    } catch (e) {}
    return forms[PLURAL_INDEX[category] != null ? PLURAL_INDEX[category] : 3] || forms[3];
  }

  function format(remaining, badge) {
    var days = Math.floor(remaining / 86400);
    var hours = Math.floor((remaining % 86400) / 3600);
    var mins = Math.floor((remaining % 3600) / 60);
    var secs = remaining % 60;
    return (days > 0 ? days + ' ' + dayWord(badge, days) + ' ' : '') + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
  }

  // The PDP bar's four pills. Each value node is written only when its text actually
  // changes, so the seconds pill is the only DOM write in 59 out of 60 ticks.
  var SEGMENTS = [
    ['days', 86400, 0],
    ['hours', 3600, 24],
    ['minutes', 60, 60],
    ['seconds', 1, 60]
  ];

  function writeSegments(root, remaining) {
    for (var i = 0; i < SEGMENTS.length; i++) {
      var name = SEGMENTS[i][0];
      var unit = SEGMENTS[i][1];
      var wrap = SEGMENTS[i][2];
      var node = root.querySelector('[data-countdown-part="' + name + '"]');
      if (!node) {
        continue;
      }
      var value = Math.floor(remaining / unit);
      if (wrap) {
        value = value % wrap;
      }
      var next = pad(value);
      if (node.textContent !== next) {
        node.textContent = next;
      }
    }
  }

  // Remove the sale framing around an expired countdown. "Prihranek" (--on-sale) stays: it compares
  // the new price with the regular one, so a sale ending does not change it.
  function expire(badge) {
    var card = badge.closest('.product-item');
    if (card) {
      var saleBadges = card.querySelectorAll('.product-label--super-discount');
      for (var j = 0; j < saleBadges.length; j++) {
        saleBadges[j].remove();
      }
      var struck = card.querySelectorAll('.price--compare');
      for (var i = 0; i < struck.length; i++) {
        struck[i].remove();
      }
    }
    badge.remove();
  }

  function tick() {
    var badges = document.querySelectorAll(SELECTOR);
    if (!badges.length) {
      stop();
      return;
    }
    var now = Math.floor(Date.now() / 1000);
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var end = parseInt(badge.getAttribute('data-countdown-end'), 10);
      if (isNaN(end)) {
        continue;
      }
      var remaining = end - now;
      if (remaining <= 0) {
        expire(badge);
        continue;
      }
      if (badge.hasAttribute('data-countdown-segments')) {
        writeSegments(badge, remaining);
        continue;
      }

      var timeNode = badge.querySelector('[data-countdown-time]');
      if (timeNode) {
        var next = format(remaining, badge);
        if (timeNode.textContent !== next) {
          timeNode.textContent = next;
        }
      }
    }
  }

  function start() {
    if (timer == null && document.querySelector(SELECTOR)) {
      tick();
      timer = window.setInterval(tick, 1000);
    }
  }

  function stop() {
    if (timer != null) {
      window.clearInterval(timer);
      timer = null;
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * ------------------------------------------------------------
 * Impact counter count-up — recosi-abo
 *
 * Animates the homepage impact stats (sections/impact-counter.liquid) from 0 to
 * their target when they scroll into view. Each [data-count-target] holds the
 * merchant's display string (EU-formatted, e.g. "155.962", "22,88"); we parse the
 * numeric core, ease up to it, and re-format each frame so the running number
 * matches the store's number style exactly. Store markets are SLO + HR, so the
 * format is hard-wired European: "." groups thousands, "," is the decimal.
 *
 * The final value is server-rendered into the element, so this only ADDS motion:
 * with prefers-reduced-motion, no IntersectionObserver, or JS off entirely, the
 * correct figure is already on screen. Fires once per stat. IIFE-wrapped (Shopify
 * bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var SELECTOR = '[data-count-target]';
  var DURATION = 1600;

  function formatEU(value, decimals) {
    var fixed = value.toFixed(decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return decimals > 0 ? parts[0] + ',' + parts[1] : parts[0];
  }

  // Split "prefix<number>suffix" and read the EU-formatted numeric core.
  function parse(str) {
    var m = str.match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!m) {
      return null;
    }
    var numStr = m[2];
    var decimals = 0;
    var comma = numStr.lastIndexOf(',');
    if (comma !== -1) {
      decimals = numStr.length - comma - 1;
    }
    var numeric = parseFloat(numStr.replace(/\./g, '').replace(',', '.'));
    if (isNaN(numeric)) {
      return null;
    }
    return { prefix: m[1], suffix: m[3], target: numeric, decimals: decimals };
  }

  function animate(el) {
    var parsed = parse(el.getAttribute('data-count-target'));
    if (!parsed) {
      return;
    }
    var startTime = null;

    function render(value) {
      el.textContent = parsed.prefix + formatEU(value, parsed.decimals) + parsed.suffix;
    }

    function frame(ts) {
      if (startTime === null) {
        startTime = ts;
      }
      var progress = Math.min((ts - startTime) / DURATION, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      if (progress < 1) {
        render(parsed.target * eased);
        window.requestAnimationFrame(frame);
      } else {
        render(parsed.target);
      }
    }

    window.requestAnimationFrame(frame);
  }

  function init() {
    var els = document.querySelectorAll(SELECTOR);
    if (!els.length) {
      return;
    }
    // No IntersectionObserver, or reduced motion: leave the server-rendered value.
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          animate(entries[i].target);
          observer.unobserve(entries[i].target);
        }
      }
    }, { threshold: 0.4 });

    for (var j = 0; j < els.length; j++) {
      observer.observe(els[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
   Logo list carousel (sections/logo-list.liquid)
   Lives here rather than in a section {% raw %}{% javascript %}{% endraw %} block: no section in this
   theme uses one, so Shopify never emits the bundled section-JS asset and the
   code would silently never run. custom.js is loaded deferred from theme.liquid.
   ============================================================================ */
// Logo list carousel arrows. Scroll-snap does the actual carousel; this only adds
// desktop arrow controls and hides them when there is nothing to scroll to, so the
// section still works if this never runs. Runs once globally (Shopify concatenates
// section JS into one bundle), so it binds every instance and re-binds on section
// load in the theme editor.
(function () {
  function setup(root) {
    // Accepts either naming: the logo list came first, photo-carousel reuses the same
    // scroll-snap behaviour under neutral attribute names.
    var track = root.querySelector('[data-logo-track], [data-carousel-track]');
    // Only the track is required. Arrows and dots are independent choices in the
    // section settings, so demanding all three here meant picking Dots (which renders
    // no arrows) bailed out before the dots were ever built.
    if (!track || root.dataset.logoCarouselReady) return;
    var prev = root.querySelector('[data-logo-prev], [data-carousel-prev]');
    var next = root.querySelector('[data-logo-next], [data-carousel-next]');
    root.dataset.logoCarouselReady = 'true';

    function update() {
      if (!prev || !next) return;
      var overflowing = track.scrollWidth > track.clientWidth + 4;
      prev.hidden = !overflowing || track.scrollLeft <= 2;
      next.hidden = !overflowing ||
        track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    function step(dir) {
      track.scrollBy({ left: dir * Math.max(track.clientWidth * 0.8, 200),
                       behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    var dotsBox = root.querySelector('[data-logo-dots], [data-carousel-dots]');
    var dots = [];

    function pages() {
      return Math.max(1, Math.ceil(track.scrollWidth / Math.max(track.clientWidth, 1)));
    }
    function buildDots() {
      if (!dotsBox) return;
      var n = pages();
      if (dots.length === n) return;
      dotsBox.innerHTML = '';
      dots = [];
      for (var i = 0; i < n; i++) {
        (function (idx) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'quick-links__dot';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', String(idx + 1));
          b.addEventListener('click', function () {
            track.scrollTo({ left: idx * track.clientWidth, behavior: 'smooth' });
          });
          dotsBox.appendChild(b);
          dots.push(b);
        })(i);
      }
    }
    function syncDots() {
      if (!dots.length) return;
      var active = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      dots.forEach(function (d, i) {
        d.classList.toggle('is-selected', i === active);
        d.setAttribute('aria-selected', i === active ? 'true' : 'false');
      });
    }
    function remeasure() { buildDots(); syncDots(); update(); }
    remeasure();
    track.addEventListener('scroll', syncDots, { passive: true });
    window.addEventListener('resize', remeasure);
    // Logos are lazy loaded, so at first measure the track can be narrower than its
    // final width and report a single page. Re-measure as each image resolves.
    track.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', remeasure, { once: true });
      img.addEventListener('error', remeasure, { once: true });
    });
    window.addEventListener('load', remeasure, { once: true });

    // Auto rotate. Skipped entirely when the viewer prefers reduced motion — an
    // auto-advancing carousel is exactly the moving content WCAG 2.2.2 is about —
    // and paused on hover or keyboard focus so it cannot move out from under someone
    // reading or tabbing through it.
    if (root.dataset.autoplay !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var delay = (parseFloat(root.dataset.cycleSpeed) || 4) * 1000;
    var timer = null;

    function advance() {
      var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      if (atEnd) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        step(1);
      }
    }
    function start() {
      if (timer || track.scrollWidth <= track.clientWidth + 4) return;
      timer = setInterval(advance, delay);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);
    root.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });
    start();
  }
  function init(scope) {
    (scope || document).querySelectorAll('[data-logo-carousel], [data-carousel]').forEach(setup);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

/* ============================================================================
   RECOSI — product spec tabs (sections/product-spec-tabs.liquid)
   ----------------------------------------------------------------------------
   The section renders every panel visible with its own heading, so with JS off the
   page is a readable stacked list. That matters more than usual here: the spec table
   is the main thing this page ranks on, so it must never depend on a script running.

   This upgrades that markup to a real tablist. Nothing is hidden until JS has actually
   run, which also avoids the flash of an empty panel if the script is slow.

   Keyboard behaviour follows the WAI-ARIA tabs pattern: Left/Right move between tabs,
   Home/End jump to the ends, and only the active tab is in the tab order so Tab moves
   past the strip into the panel rather than through every tab.
   ========================================================================== */
(function () {
  'use strict';

  function activate(strip, index) {
    var tabs = strip.querySelectorAll('[role="tab"]');
    for (var i = 0; i < tabs.length; i++) {
      var selected = i === index;
      var panel = document.getElementById(tabs[i].getAttribute('aria-controls'));
      tabs[i].setAttribute('aria-selected', selected ? 'true' : 'false');
      tabs[i].setAttribute('tabindex', selected ? '0' : '-1');
      tabs[i].classList.toggle('is-active', selected);
      if (panel) panel.hidden = !selected;
    }
  }

  function setup(strip) {
    if (strip.hasAttribute('data-spec-tabs-ready')) return;
    strip.setAttribute('data-spec-tabs-ready', '');

    var tabs = strip.querySelectorAll('[role="tab"]');
    if (tabs.length < 2) {
      // A single tab is not a tab: leave the panel open and the strip out of the way
      // rather than rendering one clickable thing that does nothing.
      strip.hidden = true;
      return;
    }

    // The per-panel headings exist only for the no-JS case; with a tab strip above them
    // they are a duplicate label.
    var fallbackTitles = document.querySelectorAll('[data-spec-tabs-fallback-title]');
    for (var f = 0; f < fallbackTitles.length; f++) fallbackTitles[f].hidden = true;

    activate(strip, 0);

    strip.addEventListener('click', function (event) {
      var tab = event.target.closest('[role="tab"]');
      if (!tab) return;
      activate(strip, Array.prototype.indexOf.call(tabs, tab));
    });

    strip.addEventListener('keydown', function (event) {
      var current = Array.prototype.indexOf.call(tabs, document.activeElement);
      if (current === -1) return;
      var next = null;
      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      activate(strip, next);
      tabs[next].focus();
    });
  }

  function init(root) {
    var scope = root || document;
    var strips = scope.querySelectorAll('[data-spec-tabs]');
    for (var i = 0; i < strips.length; i++) setup(strips[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  // Re-run when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();

// RECOSI: a product link ending in #napisi-mnenje (review request e-mails) opens the reviews tab and
// Judge.me's review form. See docs/PR-feat-review-link-2026-10-19.md.
(function () {
  'use strict';
  var HASH = '#napisi-mnenje';

  function openReviewForm() {
    if (window.location.hash !== HASH) return;
    var panel = document.querySelector('.spec-tabs__panel[id*="judge_me"]');
    if (!panel) {
      var widget = document.querySelector('.jdgm-widget');
      panel = widget && widget.closest('.spec-tabs__panel');
    }
    if (!panel) return;
    var tab = document.querySelector('[role="tab"][aria-controls="' + panel.id + '"]');
    if (tab) tab.click();
    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Judge.me draws its widget after load: the current one opens a modal, the older one an inline form
    // that its link toggles, so click only while the form is closed.
    var tries = 0;
    var timer = setInterval(function () {
      var button = panel.querySelector('[data-testid="write-review-button"], .jdgm-write-rev-link');
      if (!button && ++tries < 60) return;
      clearInterval(timer);
      if (!button) return;
      var form = document.querySelector('.jdgm-write-review-modal, .jdgm-form-wrapper');
      if (!form || window.getComputedStyle(form).display === 'none') button.click();
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(openReviewForm, 0); });
  } else {
    setTimeout(openReviewForm, 0);
  }
  window.addEventListener('hashchange', openReviewForm);
})();

/**
 * ------------------------------------------------------------
 * PDP sticky add-to-cart bar — RECOSI
 *
 * Drives snippets/product-sticky-bar.liquid: mirrors the configured total into the bar,
 * appends "· izbrana konfiguracija" once a configurator is actually present, and forwards
 * the bar's button to the real add-to-cart.
 *
 * THE BUTTON IS A PROXY. It calls .click() on the real <buy-button>'s button — the one E3
 * relocated into the configurator footer — so E6 intercepts it exactly as if the customer
 * had clicked that button directly. Everything E6 owns therefore applies unchanged: the
 * nested /cart/add.js payload, the validity gate that blocks an unbuildable configuration,
 * the disabled state, the inline error slot, and the edit-mode relabelling. A real second
 * button would need its own copy of all five, and the copy would drift from the original
 * the first time one of them changed.
 *
 * THE TOTAL MIRRORS E4. Copied from [data-configurator-total-price]'s rendered text rather
 * than recomputed: E4 owns the price engine and the store money format, and a second
 * formatter here would disagree with it on separators or currency the moment a market
 * changed. On a product with no configurator the server-rendered base price stands and
 * nothing overwrites it.
 *
 * IT REVEALS AFTER YOU PASS THE CTA. The design pins it permanently, but while the real
 * add-to-cart is on screen the bar is a duplicate costing 60px of page. An
 * IntersectionObserver on the real button drives `data-visible` — observing the button
 * rather than a scroll offset, because a pixel threshold is wrong on every viewport and
 * drifts as the configurator grows. It checks DIRECTION as well as visibility: the button is
 * ~2300px down the page, so "not visible" alone showed the bar from the very top.
 *
 * IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  var BAR = '[data-product-sticky-bar]';

  function realButton(bar) {
    var form = document.getElementById(bar.getAttribute('data-product-form'));
    if (!form) return null;
    var buyButton = form.querySelector('buy-button');
    return buyButton ? buyButton.querySelector('button') : null;
  }

  // Mirror E4's configured total. Reads the rendered text, so whatever E4 decided the
  // amount is, this says the same thing.
  function syncTotal(bar, configurator) {
    var slot = bar.querySelector('[data-sticky-total]');
    if (!slot || !configurator) return;
    var amount = configurator.querySelector('[data-configurator-total-price]');
    var text = amount ? (amount.textContent || '').trim() : '';
    if (text && slot.textContent.trim() !== text) slot.textContent = text;
  }

  // "Na zalogi · izbrana konfiguracija" — the second half only when there is a
  // configurator to configure, so a plain product's bar does not claim one.
  function syncConfigured(bar, configurator) {
    var slot = bar.querySelector('[data-sticky-configured]');
    if (!slot) return;
    var label = (configurator && configurator.labels)
      ? (configurator.labels.sticky_configured || '') : '';
    if (!label) { slot.hidden = true; return; }
    slot.textContent = ' \u00b7 ' + label;
    slot.hidden = false;
  }

  function configuratorFor(bar) {
    var form = document.getElementById(bar.getAttribute('data-product-form'));
    return form ? form.querySelector('configurator-form') : null;
  }

  function sync(bar) {
    var configurator = configuratorFor(bar);
    syncTotal(bar, configurator);
    syncConfigured(bar, configurator);
  }

  // REVEAL ON SCROLL, not always-on (client, 2026-09-10, agreeing with the recommendation).
  // The design pins it permanently; the point of the bar is to keep the CTA reachable, and
  // while the real button is on screen the bar is 60px of duplicate taking space from the
  // page. So it shows only once the real add-to-cart has left the viewport.
  //
  // Observed on the real button rather than on a scroll offset: a pixel threshold would be
  // wrong on every viewport and would drift the moment the buy column's height changed —
  // which, with a configurator that grows as groups open, is constantly. The button's own
  // visibility is the actual question being asked.
  //
  // No IntersectionObserver (very old browsers) => the bar stays visible, which is the safe
  // failure: a redundant bar beats an unreachable button.
  function watchReveal(bar) {
    var target = realButton(bar) || document.getElementById(bar.getAttribute('data-product-form'));
    if (!target || !window.IntersectionObserver) {
      bar.setAttribute('data-visible', 'true');
      return;
    }
    // DIRECTION MATTERS. "Not visible" is true both before you reach the button and after
    // you scroll past it, and the first case is most of the page: the real add-to-cart is at
    // the bottom of the configurator card, roughly 2300px down, so keying on visibility
    // alone showed the bar from the very top — which is what the client saw and read as
    // "immediately".
    //
    // `boundingClientRect.top < 0` is the test that separates the two: the button's top edge
    // is above the viewport, so it has been passed. Before you get there its top is a large
    // positive number and the bar stays down.
    // ON A PHONE, DIRECTION DOES NOT MATTER (client, 2026-09-19: price, stock and the buy
    // button must be reachable straight away). The button is ~1600px down a 844px screen, so
    // waiting until it has been passed hides the bar for the whole page.
    var phone = window.matchMedia('(max-width: 640px)');
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        var passed = !entry.isIntersecting && (phone.matches || entry.boundingClientRect.top < 0);
        bar.setAttribute('data-visible', passed ? 'true' : 'false');
      }
    }, { threshold: 0 });
    observer.observe(target);
    bar._stickyObserver = observer;
  }

  function wire(bar) {
    if (bar._stickyWired) return;
    bar._stickyWired = true;

    var proxy = bar.querySelector('[data-sticky-add]');
    if (proxy) {
      proxy.addEventListener('click', function () {
        var target = realButton(bar);
        // No real button (no configurator on this product, or the form is not there yet):
        // fall back to submitting the form, which is the plain Warehouse add.
        if (target) { target.click(); return; }
        var form = document.getElementById(bar.getAttribute('data-product-form'));
        if (form) form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }
    sync(bar);
    watchReveal(bar);
  }

  function sweep() {
    var bars = document.querySelectorAll(BAR);
    Array.prototype.forEach.call(bars, wire);
  }

  document.addEventListener('configurator:ready', sweep);
  document.addEventListener('configurator:change', function () {
    var bars = document.querySelectorAll(BAR);
    Array.prototype.forEach.call(bars, sync);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/**
 * ------------------------------------------------------------
 * Collection facet disclosure — RECOSI
 *
 * Reveals the hidden tail of a long filter group when "Prikaži vse (N)" is clicked.
 *
 * ALL the decisions are in Liquid now (sections/main-collection.liquid): which values to
 * hide, whether to collapse at all, and the translated label with its count. This file
 * only flips `hidden`.
 *
 * It used to do the whole job in JS, and that was wrong in a way worth recording: to get
 * the label it read the CONFIGURATOR's `show_all` string out of that component's data
 * island — and a collection page has no configurator, so it fell through to a count-only
 * fallback and the button rendered as a bare "(16)". Liquid already had both the count and
 * the translation; reaching into another component's island for a string was the mistake,
 * not the fallback.
 *
 * Progressive enhancement note: without JS the tail stays hidden. That is deliberate over
 * the alternative — the values are in the DOM for crawlers, and a facet list of 22 values
 * with no way to shorten it is worse for a person than one that needs a click.
 *
 * Delegated from the document so it survives Warehouse replacing the rail's markup on every
 * filter change. A per-button listener would work until the first click and then silently
 * stop, which is exactly the class of bug this file has already had once.
 *
 * IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-facet-show-all]');
    if (!button) return;

    var group = button.closest('.collection__filter-group');
    var list = group ? group.querySelector('.collection__filter-checkbox-list') : null;
    if (!list) return;

    var rows = list.children;
    for (var i = 0; i < rows.length; i++) {
      rows[i].hidden = false;
    }
    list.classList.remove('is-collapsed');

    // THE CONTAINER MUST BE UNPINNED or nothing appears to happen. Warehouse's
    // `.collection__filter-collapsible` is `overflow: hidden` with an INLINE height that its
    // own JS measures when the group is opened. Revealing ten more rows makes the content
    // taller than that measurement, so the new rows are clipped and the only visible effect
    // is the button vanishing — which is exactly what the client reported as "nothing
    // happens". Clearing the height lets the group grow to its new content.
    var collapsible = button.closest('.collection__filter-collapsible');
    if (collapsible) {
      collapsible.style.height = 'auto';
      collapsible.style.overflow = 'visible';
    }

    button.remove();
  });
})();

/**
 * ------------------------------------------------------------
 * Collection copy toggle — RECOSI
 *
 * "Pokaži več" on the category description, from snippets/collection-copy.liquid — used by
 * the Collection header section and by the built-in header inside main-collection.
 *
 * NOT Warehouse's `expandable-content`, which collapses by measured HEIGHT — the client
 * asked for a cut at a CHARACTER budget (655, with spaces), and a height cannot express
 * that: the same copy is two lines on a phone and six on a desktop. The Liquid splits the
 * copy on paragraph boundaries against that budget and emits the overflow already hidden;
 * this only flips it.
 *
 * Progressive enhancement: with no JS the opening paragraphs show and the rest stays
 * hidden. That is a deliberate choice over the alternative — showing everything — because
 * the copy is SEO body text of unbounded length, and a page that dumps 4000 characters
 * under the title on a phone is worse than one that shows 655 of them. The full text is
 * still in the DOM for crawlers either way.
 *
 * IIFE-wrapped (Shopify bundles theme JS — no globals).
 * ------------------------------------------------------------
 */
(function () {
  'use strict';

  function wire(button) {
    if (button._copyWired) return;
    button._copyWired = true;

    var wrapper = button.closest('[data-collection-copy]');
    var rest = wrapper ? wrapper.querySelector('[data-collection-copy-rest]') : null;
    if (!rest) { button.hidden = true; return; }

    button.addEventListener('click', function () {
      var open = !rest.hidden;
      rest.hidden = open;
      button.textContent = open
        ? (button.getAttribute('data-more') || '')
        : (button.getAttribute('data-less') || '');
      button.setAttribute('aria-expanded', open ? 'false' : 'true');
    });

    button.setAttribute('aria-expanded', 'false');
  }

  function sweep() {
    var buttons = document.querySelectorAll('[data-collection-copy-toggle]');
    Array.prototype.forEach.call(buttons, wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/**
 * Catalogue menu opens on hover (client comment 6).
 * Warehouse binds [data-action="toggle-menu"] to click only, whatever
 * desktop_navigation_open_trigger says. See docs/liquid-gotchas.md.
 */
(function () {
  var CLOSE_DELAY = 180;

  function wire(wrapper) {
    if (wrapper.hasAttribute('data-hover-nav-wired')) return;

    var button = wrapper.querySelector('[data-action="toggle-menu"]');
    if (!button) return;

    wrapper.setAttribute('data-hover-nav-wired', '');
    var timer = null;

    function isOpen() {
      return button.getAttribute('aria-expanded') === 'true';
    }

    wrapper.addEventListener('mouseenter', function () {
      if (timer) { clearTimeout(timer); timer = null; }
      if (!isOpen()) button.click();
    });

    wrapper.addEventListener('mouseleave', function () {
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        timer = null;
        if (isOpen()) button.click();
      }, CLOSE_DELAY);
    });
  }

  function sweep() {
    var header = document.querySelector('[data-section-type="header"]');
    if (!header) return;

    var settings = {};
    try {
      settings = JSON.parse(header.getAttribute('data-section-settings') || '{}');
    } catch (e) {
      return;
    }
    if (settings.desktopOpenTrigger !== 'hover') return;
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var wrappers = header.querySelectorAll('.header__desktop-nav');
    Array.prototype.forEach.call(wrappers, wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }

  // The header re-renders in the theme editor on every setting change, which drops the
  // listeners with the old DOM.
  document.addEventListener('shopify:section:load', sweep);
})();

/**
 * CART PAGE CONTROLS — build details toggle + discount code.
 * Both traps are in docs/liquid-gotchas.md. IIFE-wrapped (Shopify bundles theme JS).
 */
(function () {
  'use strict';

  // Own toggle: toggle-collapsible only finds a panel that is a sibling of its button.
  // aria-hidden is the single source of truth, and the CSS reads it.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-build-details-toggle]');
    if (!toggle) return;
    event.preventDefault();

    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!panel) return;

    var open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.setAttribute('aria-hidden', open ? 'true' : 'false');
  });

  // /cart/update.js reports discount_codes[].applicable, so a wrong code gets a message. See docs/liquid-gotchas.md.
  function updateDiscount(code) {
    return fetch(window.routes.cartUrl + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: code })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart update ' + response.status);
      return response.json();
    });
  }

  function showMessage(root, text) {
    var message = root.querySelector('[data-cart-discount-message]');
    if (!message) return;
    message.textContent = text;
    message.hidden = !text;
  }

  function applyDiscount(root) {
    var input = root.querySelector('[data-cart-discount-input]');
    var code = ((input && input.value) || '').trim();

    if (!code) {
      if (input) input.focus();
      return;
    }

    showMessage(root, '');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    updateDiscount(code).then(function (cart) {
      var match = (cart.discount_codes || []).filter(function (entry) {
        return entry.code.toLowerCase() === code.toLowerCase();
      })[0];

      if (match && match.applicable) {
        window.location.reload();
        return;
      }

      // Drop the dead code so it does not sit on the cart and resurface at checkout.
      var kept = (cart.discount_codes || []).filter(function (entry) { return entry.applicable; })
        .map(function (entry) { return entry.code; }).join(',');
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      showMessage(root, root.getAttribute('data-invalid-text').replace('__CODE__', code));
      return updateDiscount(kept);
    }).catch(function () {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      showMessage(root, root.getAttribute('data-error-text'));
    });
  }

  document.addEventListener('click', function (event) {
    var remove = event.target.closest('[data-cart-discount-remove]');
    if (!remove) return;
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('theme:loading:start'));
    updateDiscount('').then(function () { window.location.reload(); });
  });

  document.addEventListener('click', function (event) {
    var apply = event.target.closest('[data-cart-discount-apply]');
    if (!apply) return;
    event.preventDefault();
    applyDiscount(apply.closest('[data-cart-discount]'));
  });

  // The field sits inside the recap <form>, so Enter would post to /cart and lose the code.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.matches('[data-cart-discount-input]')) return;
    event.preventDefault();
    applyDiscount(event.target.closest('[data-cart-discount]'));
  });
})();


/* Keep configurator components out of the search RESULT COUNT, not merely out of the grid
   (sections/main-search.liquid already skips them when rendering). Shopify counts what it
   matched, so the exclusion has to be in the query. `<scope> AND <terms>` is Warehouse's own
   convention and its `filtered_terms` already strips the prefix back off for display. */
(function () {
  var EXCLUDE = '-tag:component AND ';
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.getAttribute('action') === null) return;
    if (form.getAttribute('action').indexOf('/search') === -1) return;
    var field = form.querySelector('input[name="q"]');
    if (!field || !field.value || field.value.indexOf(EXCLUDE) === 0) return;
    field.value = EXCLUDE + field.value;
  }, true);
})();


/* Position the (Nov) popover under its trigger. The top layer has no anchor of its own, so
   without this a popover opens centred in the viewport. Recalculated on open rather than
   cached, because the price row moves with the sticky header. */
(function () {
  var lastTrigger = null;
  var uid = 0;

  // The same product on a page twice (homepage sections) repeats the id, and the second
  // button would open the first bubble. Give the pressed button its own sibling bubble.
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('.price__info[popovertarget]');
    if (!trigger) return;
    var bubble = trigger.nextElementSibling;
    if (bubble && bubble.classList.contains('price__info-bubble') && document.getElementById(trigger.getAttribute('popovertarget')) !== bubble) {
      bubble.id = 'price-info-u' + (++uid);
      trigger.setAttribute('popovertarget', bubble.id);
    }
    lastTrigger = trigger;
  }, true);

  function place(popover) {
    var trigger = lastTrigger && lastTrigger.getAttribute('popovertarget') === popover.id
      ? lastTrigger : document.querySelector('[popovertarget="' + popover.id + '"]');
    if (!trigger) return;
    var r = trigger.getBoundingClientRect();
    popover.style.top = (r.bottom + 8) + 'px';
    popover.style.left = '0px';
    var w = popover.getBoundingClientRect().width;
    // Clamp to the viewport so it never hangs off either edge on a phone.
    popover.style.left = Math.max(16, Math.min(r.left, window.innerWidth - w - 16)) + 'px';
  }

  document.addEventListener('toggle', function (event) {
    var el = event.target;
    if (el.classList && el.classList.contains('price__info-bubble') && event.newState === 'open') {
      place(el);
    }
  }, true);

  /* Browsers without the popover API (pre-2023 Safari/Firefox) ignore the attribute entirely,
     so the button would do nothing at all. Toggle a class instead. */
  if (typeof HTMLElement === 'undefined' || !HTMLElement.prototype.togglePopover) {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[popovertarget]');
      document.querySelectorAll('.price__info-bubble.is-open').forEach(function (b) {
        if (!trigger || b.id !== trigger.getAttribute('popovertarget')) b.classList.remove('is-open');
      });
      if (!trigger) return;
      var bubble = document.getElementById(trigger.getAttribute('popovertarget'));
      if (!bubble) return;
      bubble.classList.toggle('is-open');
      if (bubble.classList.contains('is-open')) place(bubble);
    });
  }
})();

/* ── Cart consent gate (client comment 118) ────────────────────────────────────
   The checkout button is inert until the terms box is ticked, and the tick is written to a
   cart attribute so it lands on the order as evidence and survives Warehouse's cart
   re-render (Liquid renders `checked` back from the attribute). */
(function () {
  function sync() {
    var box = document.querySelector('[data-cart-terms-checkbox]');
    var button = document.querySelector('[data-cart-terms-submit]');
    if (!box || !button) return;
    var locked = !box.checked;
    button.disabled = locked;
    button.setAttribute('aria-disabled', String(locked));
    button.classList.toggle('is-consent-locked', locked);
  }

  document.addEventListener('change', function (event) {
    var box = event.target.closest && event.target.closest('[data-cart-terms-checkbox]');
    if (!box) return;
    var wrapper = box.closest('[data-cart-terms]');
    var key = wrapper && wrapper.getAttribute('data-cart-terms');
    var value = box.checked ? new Date().toISOString() : '';
    var hidden = wrapper && wrapper.querySelector('[data-cart-terms-value]');
    if (hidden) hidden.value = value;
    sync();
    if (!key) return;
    var attributes = {};
    attributes[key] = value;
    var url = (window.routes && window.routes.cartUrl ? window.routes.cartUrl : '/cart') + '/update.js';
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: attributes })
    });
  });

  document.addEventListener('cart:rerendered', sync);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();

/* Price filter: mirror the two range thumbs into the summary line. RECOSI */
(function () {
  document.addEventListener('input', function (event) {
    var range = event.target.closest && event.target.closest('.price-range__range-group input');
    if (!range) return;
    var root = range.closest('price-range');
    var summary = root && root.querySelector('[data-price-range-summary]');
    if (!summary) return;
    var inputs = root.querySelectorAll('.price-range__range-group input');
    summary.querySelector('[data-price-range-min]').textContent = inputs[0].value;
    summary.querySelector('[data-price-range-max]').textContent = inputs[1].value;
  });
})();

/* Cart company + VAT number: show on demand, save as cart attributes, flag a malformed number. RECOSI */
(function () {
  var VAT = /^(SI)?\d{8}$|^(HR)?\d{11}$/i;

  function save(root) {
    var inputs = root.querySelectorAll('[data-cart-company-input]');
    var attributes = {};
    inputs.forEach(function (input) {
      attributes[input.name.replace(/^attributes\[|\]$/g, '')] = input.value.trim();
    });
    fetch(window.routes.cartUrl + '/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ attributes: attributes })
    });
  }

  function check(root) {
    var vat = root.querySelector('[data-cart-company-vat]');
    var hint = root.querySelector('[data-cart-company-hint]');
    var value = vat.value.replace(/[\s.-]/g, '');
    var bad = value !== '' && !VAT.test(value);
    hint.hidden = !bad;
    // Warehouse paints any [aria-invalid] empty field red, even "false". See docs/liquid-gotchas.md.
    if (bad) vat.setAttribute('aria-invalid', 'true'); else vat.removeAttribute('aria-invalid');
  }

  function checkAll() {
    document.querySelectorAll('[data-cart-company]').forEach(check);
  }
  document.addEventListener('cart:rerendered', checkAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkAll);
  else checkAll();

  document.addEventListener('change', function (event) {
    var root = event.target.closest && event.target.closest('[data-cart-company]');
    if (!root) return;
    if (event.target.matches('[data-cart-company-toggle]')) {
      var fields = root.querySelector('[data-cart-company-fields]');
      fields.hidden = !event.target.checked;
      if (!event.target.checked) {
        root.querySelectorAll('[data-cart-company-input]').forEach(function (input) { input.value = ''; });
        check(root);
      }
    } else {
      check(root);
    }
    save(root);
  });
})();

// Leanpay's product widget follows variant changes on products without a configurator; the
// configurator reports its own total in renderTotal(). See docs/leanpay-gls.md.
(function () {
  document.addEventListener('variant:changed', function (event) {
    if (document.querySelector('script[data-configurator-data]')) return;
    var variant = event.detail && event.detail.variant;
    if (variant && variant.price) {
      window.dispatchEvent(new CustomEvent('leanpay:priceChange', { detail: { price: variant.price / 100 } }));
    }
  });
})();

// Drift zoom only follows the mouse, so scrolling under the sticky photo left its lens behind:
// replay the last pointer on scroll, and keep small photos from opening it. See docs/liquid-gotchas.md.
(function () {
  var last = null;
  document.addEventListener('mousemove', function (event) {
    last = event.target.classList && event.target.classList.contains('product-gallery__image') ? event : null;
  }, { passive: true });

  window.addEventListener('scroll', function () {
    if (!last) return;
    var rect = last.target.getBoundingClientRect();
    var inside = last.clientX >= rect.left && last.clientX <= rect.right && last.clientY >= rect.top && last.clientY <= rect.bottom;
    last.target.dispatchEvent(new MouseEvent(inside ? 'mousemove' : 'mouseleave', { clientX: last.clientX, clientY: last.clientY }));
    if (!inside) last = null;
  }, { passive: true });

  document.addEventListener('mouseenter', function (event) {
    var el = event.target;
    if (el.matches && el.matches('.product-gallery__image') && el.closest('[data-recosi-no-zoom]')) event.stopPropagation();
  }, true);
})();

// Flickity hides off-screen slides with aria-hidden but leaves their links focusable; keep them out of the tab order.
(function () {
  var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

  function sync(cell) {
    var hidden = cell.getAttribute('aria-hidden') === 'true';
    var selector = hidden ? FOCUSABLE : '[data-recosi-tabindex]';
    var targets = Array.prototype.slice.call(cell.querySelectorAll(selector));
    if (cell.matches(selector)) targets.push(cell);
    targets.forEach(function (el) {
      if (hidden) {
        if (!el.hasAttribute('data-recosi-tabindex')) el.setAttribute('data-recosi-tabindex', el.getAttribute('tabindex') || '');
        el.setAttribute('tabindex', '-1');
      } else {
        var original = el.getAttribute('data-recosi-tabindex');
        el.removeAttribute('data-recosi-tabindex');
        if (original) el.setAttribute('tabindex', original); else el.removeAttribute('tabindex');
      }
    });
  }

  function isSlide(node) {
    return node.nodeType === 1 && node.parentElement && node.parentElement.classList.contains('flickity-slider');
  }

  document.querySelectorAll('.flickity-slider > [aria-hidden]').forEach(sync);
  new MutationObserver(function (records) {
    records.forEach(function (r) { if (isSlide(r.target)) sync(r.target); });
  }).observe(document.body, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
})();

// EU energy label arrow: open the label slide in the gallery instead of a new tab (snippets/energy-label.liquid).
document.addEventListener('click', function (event) {
  var arrow = event.target.closest('[data-energy-label-arrow]');
  if (!arrow) return;
  var thumb = document.querySelector('.product-gallery__thumbnail[data-media-id="energy-label"]');
  if (!thumb) return;
  event.preventDefault();
  thumb.click();
  var gallery = thumb.closest('.product-gallery');
  if (gallery) gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// Mobile menu country/language chips: mark the tapped one at once, the reload to the other market takes a second or two.
document.addEventListener('click', function (event) {
  var choice = event.target.closest('.mobile-menu__localization-choice');
  if (!choice) return;
  var list = choice.closest('.mobile-menu__localization-list');
  if (!list) return;
  list.querySelectorAll('.mobile-menu__localization-choice').forEach(function (other) {
    other.classList.remove('is-current');
    other.removeAttribute('aria-current');
  });
  choice.classList.add('is-current');
  choice.setAttribute('aria-current', 'true');
});
